import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import UserConfigCard from "../components/UserConfigCard";

import {
  LogOut,
  Settings,
  Users,
  TrendingUp,
  DollarSign,
  Plus,
  Edit,
  Trash,
  Check,
  X,
  RefreshCw,
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  Home,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  ShoppingCart,
  TrendingDown,
  BarChart3,
  Clock3,
  BadgeDollarSign,
  CircleDollarSign,
  MessageCircle,
  LockKeyhole,
  Megaphone,
} from "lucide-react";

// ===== helpers =====
function n(v, fallback = 0) {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
}

function formatMoney(value) {
  return n(value, 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseMoneyInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatNumber(value, digits = 2) {
  const x = Number(value);
  if (!Number.isFinite(x)) return "--";
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("pt-BR");
}

function formatTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("pt-BR");
}

// parse robusto (ISO / "YYYY-MM-DD HH:mm:ss" / "DD/MM/YYYY HH:mm:ss")
function parseTimestampToMs(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts > 1e12 ? ts : ts * 1000;
  const s = String(ts).trim();
  if (!s) return null;

  // ISO / YYYY-MM-DD HH:mm:ss
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const iso = s.includes("T") ? s : s.replace(" ", "T");
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
  }

  // BR: DD/MM/YYYY ...
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    const HH = Number(m[4] || 0);
    const MI = Number(m[5] || 0);
    const SS = Number(m[6] || 0);
    const ms = new Date(yyyy, mm - 1, dd, HH, MI, SS).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

function ageSecondsFromTs(ts) {
  const ms = parseTimestampToMs(ts);
  if (!Number.isFinite(ms) || ms === null) return null;
  const s = Math.floor((Date.now() - ms) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}

function getLockAdminApiUrl() {
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "cofy.app" || host.endsWith(".cofy.app")) {
      return `${window.location.origin}/api/lock/admin.php`;
    }
  } catch (_) {}

  // Em desenvolvimento local, continua consultando o backend oficial.
  return "https://cofy.app/api/lock/admin.php";
}


function lockDateTimeInputValue(value) {
  if (!value) return "";
  const ms = parseTimestampToMs(value);
  if (!Number.isFinite(ms) || ms === null) return "";

  const d = new Date(ms);
  const pad = (v) => String(v).padStart(2, "0");

  return [
    d.getFullYear(),
    "-",
    pad(d.getMonth() + 1),
    "-",
    pad(d.getDate()),
    "T",
    pad(d.getHours()),
    ":",
    pad(d.getMinutes()),
    ":",
    pad(d.getSeconds()),
  ].join("");
}

function lockNowInputValue() {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return [
    d.getFullYear(),
    "-",
    pad(d.getMonth() + 1),
    "-",
    pad(d.getDate()),
    "T",
    pad(d.getHours()),
    ":",
    pad(d.getMinutes()),
    ":",
    pad(d.getSeconds()),
  ].join("");
}

function lockShiftDateInput(value, days) {
  const baseMs = parseTimestampToMs(value);
  const d = Number.isFinite(baseMs) && baseMs !== null ? new Date(baseMs) : new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return lockDateTimeInputValue(d.getTime());
}

function lockStatusLabel(value) {
  const s = String(value || "").toUpperCase();
  const labels = {
    DRAFT: "Rascunho",
    SIGNED: "Assinado",
    ACTIVE: "Ativo",
    MATURED: "Vencido",
    SETTLED: "Liquidado",
    CANCELLED: "Cancelado",
    CANCELED: "Cancelado",
  };
  return labels[s] || s || "—";
}

function lockStatusClass(value) {
  const s = String(value || "").toUpperCase();
  if (s === "ACTIVE") return "text-emerald-300";
  if (s === "SIGNED") return "text-amber-300";
  if (s === "MATURED") return "text-orange-300";
  if (s === "SETTLED") return "text-cyan-300";
  if (s === "CANCELLED" || s === "CANCELED") return "text-red-300";
  return "text-slate-300";
}

export default function AdminDashboard({ user, onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [activeAdminTab, setActiveAdminTab] = useState("home");

  // ===== Lista compacta de usuários =====
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersQuery, setUsersQuery] = useState("");
  const [usersSearchInput, setUsersSearchInput] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [deposits, setDeposits] = useState([]);

  // Saques
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawStatus, setWithdrawStatus] = useState("pending"); // pending | paid | rejected | all

  // Cotações (bolsa + dólar)
  const [market, setMarket] = useState(null);

  // Histórico / auditoria de negociações
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesPeriod, setTradesPeriod] = useState("30d");
  const [tradesStatus, setTradesStatus] = useState("all");
  const [tradesSide, setTradesSide] = useState("all");
  const [tradesPage, setTradesPage] = useState(1);
  const [tradesTotalPages, setTradesTotalPages] = useState(1);
  const [tradesSummary, setTradesSummary] = useState({
    volume: 0,
    client_pl: 0,
    house_gross_pl: 0,
    partner_commission: 0,
    house_net_pl: 0,
  });

  // ===== COFY LOCK • administração =====
  const [lockContracts, setLockContracts] = useState([]);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockStatus, setLockStatus] = useState("all");
  const [lockCoffee, setLockCoffee] = useState("all");
  const [lockPage, setLockPage] = useState(1);
  const [lockTotalPages, setLockTotalPages] = useState(1);
  const [lockSummary, setLockSummary] = useState({
    total: 0,
    active: 0,
    signed: 0,
    matured: 0,
    settled: 0,
    cancelled: 0,
    protected_amount: 0,
    fee_amount: 0,
  });
  const [lockActionId, setLockActionId] = useState(null);

  // Política / limite individual do COFY LOCK por usuário.
  const [lockUserPolicies, setLockUserPolicies] = useState({});
  const [lockUserPolicyLoading, setLockUserPolicyLoading] = useState(null);
  const [lockUserLimitInputs, setLockUserLimitInputs] = useState({});
  const [lockUserLimitSaving, setLockUserLimitSaving] = useState(null);

  // Ficha completa de um LOCK aberto no Admin.
  const [lockExpandedId, setLockExpandedId] = useState(null);
  const [lockDetail, setLockDetail] = useState(null);
  const [lockDetailLoading, setLockDetailLoading] = useState(false);
  const [lockMaturityInput, setLockMaturityInput] = useState("");
  const [lockMaturityReason, setLockMaturityReason] = useState("");

  // ===== Comunicado para todos os usuários =====
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastPriority, setBroadcastPriority] = useState("normal");
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastProgress, setBroadcastProgress] = useState({ sent: 0, total: 0, failed: 0 });

  // UX “profissional”
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshMs, setRefreshMs] = useState(15000);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [lastRefreshOk, setLastRefreshOk] = useState(true);
  const [refreshingNow, setRefreshingNow] = useState(false);

  // WhatsApp / atendimento
  const [waConversations, setWaConversations] = useState([]);
  const [waStats, setWaStats] = useState({ total: 0, ai: 0, waiting_human: 0, human: 0 });
  const [waSelected, setWaSelected] = useState(null);
  const [waMessages, setWaMessages] = useState([]);
  const [waLoading, setWaLoading] = useState(false);
  const [waDraft, setWaDraft] = useState("");
  const [waSending, setWaSending] = useState(false);

  const [editingContract, setEditingContract] = useState(null);
  const [newContract, setNewContract] = useState({
    symbol: "",
    name: "",
    spread: 0,
    active: true,
  });

  // Headers auth (caso axios.defaults não esteja setado)
  const authConfig = useMemo(() => {
    const t = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
  }, []);

  // ===== fetchers (silenciosos por padrão) =====
  const fetchDashboard = async () => {
    const r = await axios.get(`${API}/admin/dashboard`, authConfig);
    const data = r.data || {};
    setDashboard({
      ...data,
      total_users: data.total_users ?? 0,
      total_balance: data.total_balance ?? 0,
      house_profit: data.house_profit ?? 0,
      total_deposit_amount: data.total_deposit_amount ?? 0,
      brl_available: data.brl_available ?? 0,
      total_balance: data.total_balance ?? data.brl_available ?? 0,
      open_buy_total: data.open_buy_total ?? 0,
      open_sell_total: data.open_sell_total ?? 0,
      open_orders_total: data.open_orders_total ?? 0,
      open_buy_count: data.open_buy_count ?? data.buy_open_count ?? data.open_buys_count ?? 0,
      open_sell_count: data.open_sell_count ?? data.sell_open_count ?? data.open_sells_count ?? 0,
      open_orders_count: data.open_orders_count ?? data.orders_open_count ?? 0,
      house_profit_today: data.house_realized_today ?? data.house_profit_today ?? data.today_house_profit ?? null,
      portfolio_total: data.portfolio_total ?? data.total_portfolio ?? data.patrimony_total ?? null,

      risk_market_ok: Boolean(data.risk_market_ok),
      risk_fair_price: data.risk_fair_price ?? 0,
      open_buy_qty: data.open_buy_qty ?? 0,
      open_sell_qty: data.open_sell_qty ?? 0,
      net_customer_qty: data.net_customer_qty ?? 0,
      net_exposure_brl: data.net_exposure_brl ?? 0,
      hedge_side: data.hedge_side ?? "flat",
      hedge_qty_sacks: data.hedge_qty_sacks ?? 0,
      hedge_notional_brl: data.hedge_notional_brl ?? 0,

      client_open_pl: data.client_open_pl ?? 0,
      house_open_mtm: data.house_open_mtm ?? 0,
      client_realized_pl: data.client_realized_pl ?? 0,
      house_realized_gross: data.house_realized_gross ?? 0,
      house_realized_today: data.house_realized_today ?? 0,
      house_gross_including_open: data.house_gross_including_open ?? 0,
      house_net_result: data.house_net_result ?? data.house_profit ?? 0,

      spread_revenue_open: data.spread_revenue_open ?? 0,
      spread_revenue_closed: data.spread_revenue_closed ?? 0,
      spread_revenue_total: data.spread_revenue_total ?? 0,

      partner_commissions_total: data.partner_commissions_total ?? 0,
      partner_commissions_available: data.partner_commissions_available ?? 0,
      partner_commissions_paid: data.partner_commissions_paid ?? 0,

      risk_shock_1pct_brl: data.risk_shock_1pct_brl ?? 0,
      risk_shock_5pct_brl: data.risk_shock_5pct_brl ?? 0,
      risk_shock_10pct_brl: data.risk_shock_10pct_brl ?? 0,

      // Ordens abertas calculadas pelo admin.php atual do servidor
      open_positions_live: Array.isArray(data.open_positions_live) ? data.open_positions_live : [],

      users: Array.isArray(data.users) ? data.users : [],
    });
  };

  const fetchAdminUsers = async ({ page = usersPage, q = usersQuery } = {}) => {
    setUsersLoading(true);

    try {
      const r = await axios.get(`${API}/admin/users`, {
        params: {
          page,
          limit: 10,
          q,
        },
      });

      const data = r?.data ?? {};

      setAdminUsers(Array.isArray(data.rows) ? data.rows : []);
      setUsersPage(Number(data.page) || 1);
      setUsersTotal(Number(data.total) || 0);
      setUsersTotalPages(Math.max(1, Number(data.total_pages) || 1));
    } catch (error) {
      console.error("[ADMIN USERS]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail || "Erro ao carregar usuários."
      );
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUsersSearch = (e) => {
    e?.preventDefault?.();

    const q = String(usersSearchInput || "").trim();
    setUsersQuery(q);
    setUsersPage(1);
    setExpandedUserId(null);
    fetchAdminUsers({ page: 1, q });
  };

  const handleUsersClearSearch = () => {
    setUsersSearchInput("");
    setUsersQuery("");
    setUsersPage(1);
    setExpandedUserId(null);
    fetchAdminUsers({ page: 1, q: "" });
  };

  const goToUsersPage = (page) => {
    const next = Math.min(
      Math.max(1, Number(page) || 1),
      Math.max(1, usersTotalPages)
    );

    if (next === usersPage || usersLoading) return;

    setUsersPage(next);
    setExpandedUserId(null);
    fetchAdminUsers({ page: next, q: usersQuery });
  };


  const fetchSettings = async () => {
    const r = await axios.get(`${API}/admin/settings`, authConfig);
    setSettings(r.data || {});
  };

  const fetchContracts = async () => {
    const r = await axios.get(`${API}/admin/contracts`, authConfig);
    setContracts(Array.isArray(r.data) ? r.data : []);
  };

  const fetchDeposits = async () => {
    const r = await axios.get(`${API}/admin/deposits`, authConfig);
    setDeposits(Array.isArray(r.data) ? r.data : []);
  };

  const fetchWithdrawals = async (status = withdrawStatus) => {
    const r = await axios.get(
      `${API}/admin/withdrawals?status=${encodeURIComponent(status)}`,
      authConfig
    );
    const rows = r.data?.rows;
    setWithdrawals(Array.isArray(rows) ? rows : []);
    setWithdrawStatus(status);
  };

  const fetchMarket = async () => {
    const r = await axios.get(`${API}/market/data`, authConfig);
    setMarket(r.data || null);
  };

  const fetchTrades = async ({
    page = tradesPage,
    period = tradesPeriod,
    status = tradesStatus,
    side = tradesSide,
  } = {}) => {
    setTradesLoading(true);

    try {
      const r = await axios.get(`${API}/admin/trades`, {
        ...authConfig,
        params: {
          page,
          limit: 20,
          period,
          status,
          side,
        },
      });

      const data = r?.data || {};
      setTrades(Array.isArray(data.rows) ? data.rows : []);
      setTradesPage(Number(data.page) || 1);
      setTradesTotalPages(Math.max(1, Number(data.total_pages) || 1));
      setTradesSummary({
        volume: n(data?.summary?.volume),
        client_pl: n(data?.summary?.client_pl),
        house_gross_pl: n(data?.summary?.house_gross_pl),
        partner_commission: n(data?.summary?.partner_commission),
        house_net_pl: n(data?.summary?.house_net_pl),
      });
    } catch (error) {
      console.error("[ADMIN TRADES]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail || "Erro ao carregar negociações."
      );
    } finally {
      setTradesLoading(false);
    }
  };

  const fetchLockContracts = async ({
    page = lockPage,
    status = lockStatus,
    coffee = lockCoffee,
  } = {}) => {
    setLockLoading(true);

    try {
      const r = await axios.get(getLockAdminApiUrl(), {
        ...authConfig,
        params: {
          page,
          limit: 25,
          status,
          coffee_type: coffee,
        },
        timeout: 10000,
      });

      const data = r?.data || {};
      setLockContracts(Array.isArray(data.rows) ? data.rows : []);
      setLockPage(Number(data.page) || 1);
      setLockTotalPages(Math.max(1, Number(data.total_pages) || 1));
      setLockSummary({
        total: n(data?.summary?.total),
        active: n(data?.summary?.active),
        signed: n(data?.summary?.signed),
        matured: n(data?.summary?.matured),
        settled: n(data?.summary?.settled),
        cancelled: n(data?.summary?.cancelled),
        protected_amount: n(data?.summary?.protected_amount),
        fee_amount: n(data?.summary?.fee_amount),
      });
    } catch (error) {
      console.error("[ADMIN LOCK]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail ||
        "Não foi possível carregar os contratos COFY LOCK."
      );
    } finally {
      setLockLoading(false);
    }
  };

  const fetchLockDetails = async (contractId, { silent = false } = {}) => {
    const id = Number(contractId);
    if (!id) return null;

    if (!silent) setLockDetailLoading(true);

    try {
      const r = await axios.get(getLockAdminApiUrl(), {
        ...authConfig,
        params: { contract_id: id },
        timeout: 10000,
      });

      const data = r?.data?.data || null;
      setLockDetail(data);

      const maturity =
        data?.contract?.maturity_at ||
        data?.lifecycle?.maturity_at ||
        "";

      setLockMaturityInput(lockDateTimeInputValue(maturity));
      setLockMaturityReason("");

      return data;
    } catch (error) {
      console.error("[ADMIN LOCK DETAIL]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail ||
        "Não foi possível carregar a ficha completa do LOCK."
      );
      return null;
    } finally {
      if (!silent) setLockDetailLoading(false);
    }
  };

  const fetchLockUserPolicy = async (userId, { silent = false } = {}) => {
    const id = Number(userId);
    if (!id) return null;

    if (!silent) setLockUserPolicyLoading(id);

    try {
      const r = await axios.get(getLockAdminApiUrl(), {
        ...authConfig,
        params: { user_policy: 1, user_id: id },
        timeout: 10000,
      });

      const policy =
        r?.data?.policy ||
        r?.data?.data?.policy ||
        r?.data?.data ||
        null;

      if (!policy || Number(policy?.user_id || id) !== id) {
        throw new Error("Política COFY LOCK do usuário não encontrada.");
      }

      setLockUserPolicies((current) => ({
        ...current,
        [id]: policy,
      }));

      setLockUserLimitInputs((current) => ({
        ...current,
        [id]:
          policy?.custom_limit === null ||
          policy?.custom_limit === undefined
            ? ""
            : String(policy.custom_limit),
      }));

      return policy;
    } catch (error) {
      console.error("[ADMIN LOCK USER POLICY]", error?.response?.data || error);
      if (!silent) {
        toast.error(
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível carregar o limite COFY LOCK do usuário."
        );
      }
      return null;
    } finally {
      if (!silent) setLockUserPolicyLoading(null);
    }
  };

  const saveLockUserLimit = async (userId, { reset = false } = {}) => {
    const id = Number(userId);
    if (!id || lockUserLimitSaving) return;

    let limitBrl = null;

    if (!reset) {
      const raw = String(lockUserLimitInputs?.[id] ?? "").trim();

      if (raw !== "") {
        const parsed = parseMoneyInput(raw);

        if (!Number.isFinite(parsed) || parsed < 0) {
          toast.error("Informe um limite COFY LOCK válido.");
          return;
        }

        limitBrl = Math.round(parsed * 100) / 100;
      }
    }

    try {
      setLockUserLimitSaving(id);

      const r = await axios.post(
        getLockAdminApiUrl(),
        {
          action: "set_user_limit",
          user_id: id,
          limit_brl: limitBrl,
        },
        {
          ...authConfig,
          timeout: 10000,
        }
      );

      const policy =
        r?.data?.policy ||
        r?.data?.data?.policy ||
        r?.data?.data ||
        null;

      if (policy) {
        setLockUserPolicies((current) => ({
          ...current,
          [id]: policy,
        }));

        setLockUserLimitInputs((current) => ({
          ...current,
          [id]:
            policy?.custom_limit === null ||
            policy?.custom_limit === undefined
              ? ""
              : String(policy.custom_limit),
        }));
      } else {
        await fetchLockUserPolicy(id, { silent: true });
      }

      toast.success(
        limitBrl === null
          ? "Limite automático do COFY LOCK restaurado."
          : `Limite COFY LOCK atualizado para ${formatMoney(limitBrl)}.`
      );
    } catch (error) {
      console.error("[ADMIN LOCK USER LIMIT]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível atualizar o limite COFY LOCK."
      );
    } finally {
      setLockUserLimitSaving(null);
    }
  };

  const toggleAdminUser = async (targetUser) => {
    const id = Number(targetUser?.id);
    if (!id) return;

    if (Number(expandedUserId) === id) {
      setExpandedUserId(null);
      return;
    }

    setExpandedUserId(id);
    await fetchLockUserPolicy(id);
  };

  const toggleLockDetails = async (contract) => {
    const id = Number(contract?.id);
    if (!id) return;

    if (Number(lockExpandedId) === id) {
      setLockExpandedId(null);
      setLockDetail(null);
      setLockMaturityInput("");
      setLockMaturityReason("");
      return;
    }

    setLockExpandedId(id);
    setLockDetail(null);
    setLockMaturityInput(lockDateTimeInputValue(contract?.maturity_at));
    setLockMaturityReason("");
    await fetchLockDetails(id);
  };

  const handleLockAction = async (contract, action) => {
    if (!contract?.id || lockActionId) return;

    const id = Number(contract.id);
    const number = contract.contract_number || `#${id}`;
    let payload = { action, contract_id: id };

    if (action === "confirm_hedge") {
      const hedgeReference = window.prompt(
        `Referência do hedge para ${number}:`,
        contract?.hedge_reference || ""
      );
      if (hedgeReference === null) return;

      const cleanReference = String(hedgeReference || "").trim();
      if (!cleanReference) {
        toast.error("Informe a referência do hedge.");
        return;
      }

      const hedgeNote = window.prompt(
        "Observação do hedge (opcional):",
        contract?.hedge_note || ""
      );

      payload = {
        ...payload,
        hedge_reference: cleanReference,
        hedge_note: hedgeNote === null ? "" : String(hedgeNote),
      };
    }

    if (action === "activate") {
      if (!window.confirm(
        `Ativar ${number}? Os ${Number(contract.duration_days || 90)} dias passam a contar a partir da ativação.`
      )) return;
    }

    if (action === "change_maturity") {
      const newMaturity = String(lockMaturityInput || "").trim();
      if (!newMaturity) {
        toast.error("Informe a nova data e hora de vencimento.");
        return;
      }

      const reason = String(lockMaturityReason || "").trim();
      if (!window.confirm(
        `Alterar o vencimento de ${number} para ${formatDateTime(newMaturity)}?`
      )) return;

      payload = {
        ...payload,
        new_maturity_at: newMaturity,
        reason: reason || "Alteração administrativa de vencimento.",
      };
    }

    if (action === "settle") {
      const protectedAmount = formatMoney(contract?.protected_amount);
      const spreadAmount = formatMoney(contract?.fee_amount);

      if (!window.confirm(
        `Liquidar ${number}? O sistema usará a cotação atual do café, manterá o capital protegido em caso de queda, acrescentará a valorização em caso de alta e descontará o spread de ${spreadAmount}. Valor protegido: ${protectedAmount}. Após a confirmação, o saldo líquido será creditado na carteira do cliente.`
      )) return;
    }

    if (action === "cancel") {
      const reason = window.prompt(
        `Motivo do cancelamento de ${number}:`,
        ""
      );

      if (reason === null) return;

      const cleanReason = String(reason || "").trim();
      if (!cleanReason) {
        toast.error("Informe o motivo do cancelamento.");
        return;
      }

      if (!window.confirm(
        `Cancelar ${number}? Esta ação ficará registrada no histórico do contrato.`
      )) return;

      payload = {
        ...payload,
        reason: cleanReason,
      };
    }

    try {
      setLockActionId(id);

      await axios.post(getLockAdminApiUrl(), payload, {
        ...authConfig,
        timeout: 10000,
      });

      const successMessages = {
        confirm_hedge: "Hedge confirmado.",
        activate: "Contrato COFY LOCK ativado.",
        change_maturity: "Vencimento atualizado.",
        settle: "COFY LOCK liquidado e saldo creditado.",
        cancel: "Contrato cancelado.",
        sync_maturity: "Status de vencimento sincronizado.",
      };

      toast.success(successMessages[action] || "Contrato atualizado.");

      await fetchLockContracts({
        page: lockPage,
        status: lockStatus,
        coffee: lockCoffee,
      });

      if (Number(lockExpandedId) === id) {
        await fetchLockDetails(id, { silent: true });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível atualizar o contrato COFY LOCK."
      );
    } finally {
      setLockActionId(null);
    }
  };

  const fetchWhatsAppConversations = async () => {
    setWaLoading(true);
    try {
      const r = await axios.get(`${API}/admin/whatsapp/conversations`, authConfig);
      const data = r?.data || {};
      const rows = Array.isArray(data.rows) ? data.rows : [];
      setWaConversations(rows);
      setWaStats(data.stats || { total: 0, ai: 0, waiting_human: 0, human: 0 });
      if (waSelected) {
        const fresh = rows.find((x) => Number(x.id) === Number(waSelected.id));
        if (fresh) setWaSelected(fresh);
      }
    } catch (error) {
      console.error("[ADMIN WHATSAPP]", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao carregar WhatsApp.");
    } finally {
      setWaLoading(false);
    }
  };

  const fetchWhatsAppMessages = async (conversationId) => {
    if (!conversationId) return;
    try {
      const r = await axios.get(`${API}/admin/whatsapp/conversations/${conversationId}/messages`, authConfig);
      const data = r?.data || {};
      setWaMessages(Array.isArray(data.rows) ? data.rows : Array.isArray(data.messages) ? data.messages : []);
    } catch (error) {
      console.error("[ADMIN WHATSAPP MESSAGES]", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao carregar mensagens.");
    }
  };

  const selectWhatsAppConversation = async (conversation) => {
    setWaSelected(conversation);
    setWaMessages([]);
    await fetchWhatsAppMessages(conversation.id);
  };

  const takeWhatsAppConversation = async () => {
    if (!waSelected?.id) return;
    try {
      await axios.post(`${API}/admin/whatsapp/conversations/${waSelected.id}/take`, {}, authConfig);
      toast.success("Atendimento humano assumido.");
      await fetchWhatsAppConversations();
      await fetchWhatsAppMessages(waSelected.id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível assumir o atendimento.");
    }
  };

  const returnWhatsAppToAI = async () => {
    if (!waSelected?.id) return;
    try {
      await axios.post(`${API}/admin/whatsapp/conversations/${waSelected.id}/ai`, {}, authConfig);
      toast.success("Conversa devolvida para a IA.");
      await fetchWhatsAppConversations();
      await fetchWhatsAppMessages(waSelected.id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível devolver para a IA.");
    }
  };

  const sendWhatsAppHumanMessage = async (e) => {
    e?.preventDefault?.();
    if (!waSelected?.id || waSelected.mode !== "human" || waSending) return;
    const message = String(waDraft || "").trim();
    if (!message) return;

    setWaSending(true);
    try {
      await axios.post(
        `${API}/admin/whatsapp/conversations/${waSelected.id}/send`,
        { message },
        authConfig
      );
      setWaDraft("");
      await fetchWhatsAppMessages(waSelected.id);
      await fetchWhatsAppConversations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível enviar a mensagem.");
    } finally {
      setWaSending(false);
    }
  };

  const loadAllUsersForBroadcast = async () => {
    const rows = [];
    let page = 1;
    let totalPages = 1;

    do {
      const r = await axios.get(`${API}/admin/users`, {
        ...authConfig,
        params: { page, limit: 100, q: "" },
        timeout: 8000,
      });
      const data = r?.data || {};
      const chunk = Array.isArray(data.rows) ? data.rows : [];
      rows.push(...chunk);
      totalPages = Math.max(1, Number(data.total_pages) || 1);
      page += 1;
    } while (page <= totalPages);

    const unique = new Map();
    rows.forEach((u) => {
      const id = Number(u?.id);
      if (id > 0) unique.set(id, u);
    });
    return Array.from(unique.values());
  };

  const sendBroadcastToAllUsers = async (e) => {
    e?.preventDefault?.();
    if (broadcastSending) return;

    const title = String(broadcastTitle || "").trim();
    const message = String(broadcastMessage || "").trim();

    if (!title) {
      toast.error("Informe o título do comunicado.");
      return;
    }
    if (!message) {
      toast.error("Informe a mensagem do comunicado.");
      return;
    }
    if (title.length > 150) {
      toast.error("O título pode ter no máximo 150 caracteres.");
      return;
    }
    if (message.length > 5000) {
      toast.error("A mensagem pode ter no máximo 5.000 caracteres.");
      return;
    }

    const expected = Number(usersTotal || dashboard?.total_users || 0);
    if (!window.confirm(
      `Enviar este comunicado para TODOS os usuários${expected > 0 ? ` (${expected})` : ""}?`
    )) return;

    setBroadcastSending(true);
    setBroadcastProgress({ sent: 0, total: 0, failed: 0 });

    try {
      const users = await loadAllUsersForBroadcast();
      setBroadcastProgress({ sent: 0, total: users.length, failed: 0 });

      let sent = 0;
      let failed = 0;
      const batchSize = 12;

      for (let i = 0; i < users.length; i += batchSize) {
        const batch = users.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map((u) =>
            axios.post(
              `${API}/admin/user/${u.id}/messages`,
              {
                title,
                message,
                priority: broadcastPriority,
              },
              {
                ...authConfig,
                timeout: 8000,
              }
            )
          )
        );

        results.forEach((result) => {
          if (result.status === "fulfilled") sent += 1;
          else failed += 1;
        });

        setBroadcastProgress({ sent, total: users.length, failed });
      }

      if (failed === 0) {
        toast.success(`Comunicado enviado para ${sent} usuário(s).`);
        setBroadcastTitle("");
        setBroadcastMessage("");
        setBroadcastPriority("normal");
        setBroadcastOpen(false);
      } else {
        toast.error(`Enviado para ${sent}; ${failed} envio(s) falharam.`);
      }
    } catch (error) {
      console.error("[ADMIN BROADCAST]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail ||
        "Não foi possível enviar o comunicado geral."
      );
    } finally {
      setBroadcastSending(false);
    }
  };

  // ===== refresh all (com controle de erro) =====
  const refreshNow = async (opts = { toastOnError: false }) => {
    setRefreshingNow(true);
    try {
      const results = await Promise.allSettled([
        fetchDashboard(),
        fetchDeposits(),
        fetchWithdrawals(withdrawStatus),
        fetchMarket(),
      ]);

      const ok = results.every((x) => x.status === "fulfilled");
      setLastRefreshOk(ok);
      setLastRefreshAt(new Date());

      if (!ok && opts.toastOnError) {
        toast.error("Falha ao atualizar alguns dados. Verifique o servidor.");
      }
    } catch (e) {
      setLastRefreshOk(false);
      setLastRefreshAt(new Date());
      if (opts.toastOnError) toast.error("Erro ao atualizar dados.");
    } finally {
      setRefreshingNow(false);
    }
  };

  // ===== init =====
  useEffect(() => {
    (async () => {
      try {
        await fetchDashboard();
        await fetchSettings();
        await fetchContracts();
        await fetchDeposits();
        await fetchWithdrawals("pending");
        await fetchMarket();
        setLastRefreshOk(true);
        setLastRefreshAt(new Date());
      } catch (error) {
        console.error("[AdminDashboard] init error:", error?.response?.data || error);
        setLastRefreshOk(false);
        setLastRefreshAt(new Date());
        toast.error("Erro ao carregar painel admin");
      }
    })();
  }, []);

  // ===== usuários: carga inicial =====
  useEffect(() => {
    fetchAdminUsers({ page: 1, q: "" });
  }, []);

  useEffect(() => {
    if (activeAdminTab !== "trades") return;
    fetchTrades({
      page: tradesPage,
      period: tradesPeriod,
      status: tradesStatus,
      side: tradesSide,
    });
  }, [activeAdminTab]);

  useEffect(() => {
    if (activeAdminTab !== "lock") return;
    fetchLockContracts({
      page: lockPage,
      status: lockStatus,
      coffee: lockCoffee,
    });
  }, [activeAdminTab]);

  useEffect(() => {
    if (activeAdminTab !== "whatsapp") return;
    fetchWhatsAppConversations();
  }, [activeAdminTab]);

  useEffect(() => {
    if (activeAdminTab !== "whatsapp" || !autoRefresh) return;
    const id = setInterval(() => {
      fetchWhatsAppConversations();
      if (waSelected?.id) fetchWhatsAppMessages(waSelected.id);
    }, 10000);
    return () => clearInterval(id);
  }, [activeAdminTab, autoRefresh, waSelected?.id]);

  // ===== auto refresh =====
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      refreshNow({ toastOnError: false });
    }, refreshMs);

    return () => clearInterval(id);
  }, [autoRefresh, refreshMs, withdrawStatus]);

  // ===== actions =====
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings, authConfig);
      toast.success("Configurações salvas!");
    } catch (error) {
      console.error("[AdminDashboard] save settings:", error?.response?.data || error);
      toast.error("Erro ao salvar configurações");
    }
  };

  const handleCreateContract = async () => {
    try {
      const payload = { ...newContract, spread: Number(newContract.spread ?? 0) };
      await axios.post(`${API}/admin/contracts`, payload, authConfig);
      toast.success("Contrato criado!");
      setNewContract({ symbol: "", name: "", spread: 0, active: true });
      fetchContracts();
    } catch (error) {
      console.error("[AdminDashboard] create contract:", error?.response?.data || error);
      toast.error("Erro ao criar contrato");
    }
  };

  const handleUpdateContract = async (contract) => {
    try {
      const payload = { ...contract, spread: Number(contract.spread ?? 0) };
      await axios.put(`${API}/admin/contracts/${contract.id}`, payload, authConfig);
      toast.success("Contrato atualizado!");
      setEditingContract(null);
      fetchContracts();
    } catch (error) {
      console.error("[AdminDashboard] update contract:", error?.response?.data || error);
      toast.error("Erro ao atualizar contrato");
    }
  };

  const handleDeleteContract = async (id) => {
    if (!window.confirm("Tem certeza que deseja deletar este contrato?")) return;
    try {
      await axios.delete(`${API}/admin/contracts/${id}`, authConfig);
      toast.success("Contrato deletado!");
      fetchContracts();
    } catch (error) {
      console.error("[AdminDashboard] delete contract:", error?.response?.data || error);
      toast.error("Erro ao deletar contrato");
    }
  };

  const handleConfirmDeposit = async (depositId) => {
    try {
      await axios.post(`${API}/deposit/${depositId}/confirm`, {}, authConfig);
      toast.success("Depósito confirmado!");
      fetchDeposits();
      fetchDashboard();
    } catch (error) {
      console.error("[AdminDashboard] confirm deposit:", error?.response?.data || error);
      toast.error("Erro ao confirmar depósito");
    }
  };

  const handleConfirmWithdrawal = async (withdrawalId) => {
    if (!window.confirm("Confirmar este saque como PAGO?")) return;
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/pay`, {}, authConfig);
      toast.success("Saque confirmado (pago)!");
      fetchWithdrawals(withdrawStatus);
      fetchDashboard();
    } catch (error) {
      console.error("[AdminDashboard] confirm withdrawal:", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao confirmar saque");
    }
  };

  const handleRejectWithdrawal = async (withdrawalId) => {
    if (!window.confirm("RECUSAR este saque e DEVOLVER o saldo ao usuário?")) return;
    const reason = window.prompt("Motivo (opcional):") || "";
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/reject`, { reason }, authConfig);
      toast.success("Saque recusado e saldo devolvido!");
      fetchWithdrawals(withdrawStatus);
      fetchDashboard();
    } catch (error) {
      console.error("[AdminDashboard] reject withdrawal:", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao recusar saque");
    }
  };

  const pendingDeposits = (deposits || []).filter((d) => d && d.status === "pending");
  const pendingWithdrawals = (withdrawals || []).filter((w) => w && w.status === "pending");

  // ===== market display =====
  const mktOk = !!(market && market.ok);
  const kc = mktOk ? n(market.kc_cents_per_lb, null) : null;
  const fx = mktOk ? n(market.usdtbrl, null) : null;
  const saca = mktOk ? n(market.saca_brl ?? market.brl_per_saca ?? market.price, null) : null;

  // ✅ cache: primeiro usa cache_age_seconds, se não tiver calcula via timestamps do payload
  const cacheKC =
    mktOk
      ? (
          (Number.isFinite(Number(market?.cache_age_seconds?.kc)) ? Number(market.cache_age_seconds.kc) : null) ??
          ageSecondsFromTs(market?.kc_saved_at || market?.kc_source_ts || market?.kc_data_hora)
        )
      : null;

  const cacheFX =
    mktOk
      ? (
          (Number.isFinite(Number(market?.cache_age_seconds?.fx)) ? Number(market.cache_age_seconds.fx) : null) ??
          (Number.isFinite(Number(market?.cache_age_seconds?.dol)) ? Number(market.cache_age_seconds.dol) : null) ??
          ageSecondsFromTs(market?.fx_saved_at || market?.fx_source_ts || market?.fx_data_hora)
        )
      : null;

  const pendingDepositAmount = pendingDeposits.reduce((sum, d) => sum + n(d?.amount, 0), 0);
  const pendingWithdrawalAmount = pendingWithdrawals.reduce((sum, w) => sum + n(w?.amount, 0), 0);

  const marketOpen = mktOk ? n(market?.open ?? market?.kc_open ?? market?.raw?.kc?.open, null) : null;
  const marketHigh = mktOk ? n(market?.high ?? market?.kc_high ?? market?.raw?.kc?.high, null) : null;
  const marketLow = mktOk ? n(market?.low ?? market?.kc_low ?? market?.raw?.kc?.low, null) : null;
  const marketClose = mktOk ? n(market?.close ?? market?.previous_close ?? market?.prev_close ?? market?.kc_close ?? market?.raw?.kc?.close ?? market?.raw?.kc?.previous_close, null) : null;
  const marketBid = mktOk ? n(market?.bid ?? market?.kc_bid ?? market?.raw?.kc?.bid, null) : null;
  const marketAsk = mktOk ? n(market?.ask ?? market?.kc_ask ?? market?.raw?.kc?.ask, null) : null;
  const marketChangePct = mktOk ? n(market?.change_pct ?? market?.kc_change_pct ?? market?.raw?.kc?.change_pct, 0) : 0;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-2 px-3 py-2 sm:px-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
              <Settings className="w-6 h-6 text-slate-900" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                Painel Admin
              </h1>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0 md:gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Activity className="w-4 h-4" />
              <span className="text-slate-400">Auto</span>
              <Switch checked={autoRefresh} onCheckedChange={(v) => setAutoRefresh(!!v)} />
            </div>

            <select
              value={refreshMs}
              onChange={(e) => setRefreshMs(Number(e.target.value))}
              className="px-2 py-2 bg-slate-900 border border-slate-700 rounded-md text-white text-sm"
              title="Intervalo"
            >
              <option value={10000}>10s</option>
              <option value={15000}>15s</option>
              <option value={30000}>30s</option>
              <option value={60000}>60s</option>
            </select>

            <Button
              variant="outline"
              onClick={() => refreshNow({ toastOnError: true })}
              className="border-slate-700"
              disabled={refreshingNow}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshingNow ? "animate-spin" : ""}`} />
              Atualizar agora
            </Button>

            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${lastRefreshOk ? "bg-emerald-400" : "bg-red-400"}`} />
              Última: {formatTime(lastRefreshAt)}
            </div>

            <Button variant="outline" onClick={onLogout} className="border-slate-700">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-[1500px] px-2 py-2 sm:px-4 sm:py-4">
        <div className="sticky top-[88px] z-40 -mx-2 mb-3 border-y border-slate-800 bg-slate-950/95 px-2 py-1.5 backdrop-blur sm:top-[92px] sm:mx-0 sm:rounded-xl sm:border">
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[
              ["home", "Início", Home],
              ["users", "Usuários", Users],
              ["deposits", "Depósitos", ArrowDownToLine],
              ["withdrawals", "Saques", ArrowUpFromLine],
              ["trades", "Negociações", Activity],
              ["lock", "COFY LOCK", LockKeyhole],
              ["whatsapp", "WhatsApp", MessageCircle],
              ["contracts", "Contratos", BarChart3],
              ["settings", "Config.", Settings],
            ].map(([value, label, Icon]) => (
              <button key={value} type="button" onClick={() => setActiveAdminTab(value)}
                className={`flex min-w-max items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] sm:text-xs font-black transition active:scale-95 ${
                  activeAdminTab === value ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-300" : "border-slate-800 bg-slate-900 text-slate-400 hover:text-white"
                }`}>
                <Icon className="h-4 w-4" />
                {label}
                {value === "deposits" && pendingDeposits.length > 0 && <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] text-slate-950">{pendingDeposits.length}</span>}
                {value === "withdrawals" && pendingWithdrawals.length > 0 && <span className="rounded-full bg-red-400 px-1.5 py-0.5 text-[10px] text-slate-950">{pendingWithdrawals.length}</span>}
              </button>
            ))}
          </div>
        </div>

        <Tabs value={activeAdminTab} onValueChange={setActiveAdminTab} className="space-y-3">
          <TabsList className="hidden">
            <TabsTrigger value="home">Início</TabsTrigger><TabsTrigger value="users">Usuários</TabsTrigger>
            <TabsTrigger value="deposits">Depósitos</TabsTrigger><TabsTrigger value="withdrawals">Saques</TabsTrigger>
            <TabsTrigger value="trades">Negociações</TabsTrigger><TabsTrigger value="lock">COFY LOCK</TabsTrigger><TabsTrigger value="whatsapp">WhatsApp</TabsTrigger><TabsTrigger value="contracts">Contratos</TabsTrigger><TabsTrigger value="settings">Configurações</TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="mt-0 space-y-3">
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-3 shadow-2xl sm:p-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-400"><Activity className="h-4 w-4" /> Operação em tempo real</div>
                  <h2 className="mt-1 text-xl font-black text-white">Visão geral COFY</h2>
                  <p className="mt-1 text-sm text-slate-400">Mercado, usuários, ordens e financeiro na mesma tela.</p>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-xs text-slate-400">
                  <span className={`h-2.5 w-2.5 rounded-full ${lastRefreshOk ? "animate-pulse bg-emerald-400" : "bg-red-400"}`} />
                  {autoRefresh ? `Atualiza a cada ${Math.round(refreshMs / 1000)}s` : "Atualização automática desligada"}
                </div>
              </div>
            </div>

            {dashboard ? (
              <>
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                  <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 p-3"><Users className="mb-3 h-6 w-6 text-blue-300" /><div className="text-[10px] font-black uppercase tracking-wider text-blue-200/70">Usuários</div><div className="mt-1 text-xl font-black text-white">{dashboard.total_users ?? 0}</div><div className="mt-1 text-xs text-slate-400">cadastrados</div></div>
                  <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-3"><CircleDollarSign className="mb-3 h-6 w-6 text-cyan-300" /><div className="text-[10px] font-black uppercase tracking-wider text-cyan-200/70">Depósitos</div><div className="mt-1 text-lg font-black text-white">{formatMoney(dashboard.total_deposit_amount)}</div><div className="mt-1 text-xs text-slate-400">confirmados</div></div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-900 p-3"><Wallet className="mb-3 h-6 w-6 text-emerald-300" /><div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Saldo carteiras</div><div className="mt-1 text-lg font-black text-white">{formatMoney(dashboard.brl_available)}</div><div className="mt-1 text-xs text-slate-500">saldo livre</div></div>
                  <div className={`rounded-2xl border p-3 ${n(dashboard.house_net_result) >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}><BadgeDollarSign className={`mb-3 h-6 w-6 ${n(dashboard.house_net_result) >= 0 ? "text-emerald-300" : "text-red-300"}`} /><div className="text-[10px] font-black uppercase tracking-wider text-slate-400">Resultado líquido COFY</div><div className={`mt-1 text-lg font-black ${n(dashboard.house_net_result) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{n(dashboard.house_net_result) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_net_result)))}</div><div className="mt-1 text-xs text-slate-500">realizado + aberto − parceiros</div></div>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 sm:p-3">
                  <div className="mb-4 flex items-center justify-between gap-2"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Trader aberto</div><h3 className="mt-1 text-lg font-black text-white">Operações em andamento</h3></div><div className="rounded-full border border-violet-500/25 bg-violet-500/10 px-3 py-1 text-xs font-black text-violet-300">{dashboard.open_orders_count || "—"} ordens</div></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-emerald-500/25 bg-gradient-to-b from-emerald-500/15 to-emerald-500/5 p-3"><div className="flex items-center justify-between"><div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-400 text-slate-950"><TrendingUp className="h-6 w-6" /></div><span className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Alta</span></div><div className="mt-4 text-sm font-black text-emerald-200">COMPRAS ABERTAS</div><div className="mt-1 text-xl font-black text-white">{formatMoney(dashboard.open_buy_total)}</div><div className="mt-2 text-xs text-emerald-300/70">{dashboard.open_buy_count ? `${dashboard.open_buy_count} ordem(ns)` : "Operações de compra"}</div></div>
                    <div className="rounded-2xl border border-red-500/25 bg-gradient-to-b from-red-500/15 to-red-500/5 p-3"><div className="flex items-center justify-between"><div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-400 text-slate-950"><TrendingDown className="h-6 w-6" /></div><span className="text-[10px] font-black uppercase tracking-wider text-red-300">Baixa</span></div><div className="mt-4 text-sm font-black text-red-200">VENDAS ABERTAS</div><div className="mt-1 text-xl font-black text-white">{formatMoney(dashboard.open_sell_total)}</div><div className="mt-2 text-xs text-red-300/70">{dashboard.open_sell_count ? `${dashboard.open_sell_count} ordem(ns)` : "Operações de venda"}</div></div>
                  </div>
                  <div className="mt-3 rounded-2xl border border-violet-500/20 bg-violet-500/5 p-3"><div className="flex items-center justify-between gap-2"><span className="text-xs font-bold uppercase tracking-wider text-violet-300">Total em ordens abertas</span><span className="text-xl font-black text-white">{formatMoney(dashboard.open_orders_total)}</span></div></div>
                </div>

                {/* Mesa ao vivo das posições abertas — SLIM / MOBILE FIRST */}
                <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
                  <div className="flex items-center justify-between gap-2 border-b border-slate-800 px-2.5 py-2 sm:px-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-violet-300">
                        <Activity className="h-3.5 w-3.5" /> Mesa ao vivo
                      </div>
                      <h3 className="truncate text-sm font-black text-white sm:text-base">Posições abertas</h3>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-full ${dashboard.risk_market_ok ? "animate-pulse bg-emerald-400" : "bg-amber-400"}`} />
                      <span className="text-[9px] font-bold text-slate-500 sm:text-[10px]">
                        {dashboard.risk_market_ok ? "ATUALIZADO" : "AGUARDANDO"}
                      </span>
                    </div>
                  </div>

                  {Array.isArray(dashboard.open_positions_live) && dashboard.open_positions_live.length > 0 ? (
                    <div className="divide-y divide-slate-800">
                      {/* Cabeçalho só no desktop */}
                      <div className="hidden grid-cols-[76px_minmax(150px,1.4fr)_110px_105px_105px_105px_110px_125px] gap-2 bg-slate-950/70 px-3 py-2 text-[9px] font-black uppercase tracking-wider text-slate-600 lg:grid">
                        <span>Tipo</span><span>Cliente</span><span>Valor</span><span>Entrada</span>
                        <span>Atual</span><span>Sacas</span><span>P/L cliente</span><span>Líquido COFY</span>
                      </div>

                      {dashboard.open_positions_live.map((pos) => {
                        const isBuy = String(pos?.side || "").toLowerCase() === "buy";
                        const clientPl = n(pos?.client_pl);
                        const houseNet = n(pos?.house_net_pl);
                        const userLabel = pos?.user_name || pos?.user_email || `Usuário #${pos?.user_id ?? "—"}`;

                        return (
                          <div key={pos.id} className="bg-slate-950/35 px-2 py-2 hover:bg-slate-800/35 sm:px-3">
                            {/* MOBILE: uma linha principal compacta + uma linha auxiliar */}
                            <div className="lg:hidden">
                              <div className="grid grid-cols-[58px_minmax(0,1fr)_88px_82px] items-center gap-1.5">
                                <span className={`rounded-md px-1.5 py-1 text-center text-[9px] font-black ${isBuy ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                                  {isBuy ? "COMPRA" : "VENDA"}
                                </span>
                                <div className="min-w-0">
                                  <div className="truncate text-[11px] font-bold text-slate-200">{userLabel}</div>
                                  <div className="truncate text-[8px] text-slate-600">#{pos.id} • {formatDateTime(pos?.created_at)}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-[8px] uppercase text-slate-600">Valor</div>
                                  <div className="text-[10px] font-black text-white">{formatMoney(pos?.amount)}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-[8px] uppercase text-slate-600">P/L</div>
                                  <div className={`text-[10px] font-black ${clientPl >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                    {clientPl >= 0 ? "+" : "-"}{formatMoney(Math.abs(clientPl))}
                                  </div>
                                </div>
                              </div>

                              <div className="mt-1.5 grid grid-cols-4 gap-1 border-t border-slate-800/70 pt-1.5 text-[8px]">
                                <div><span className="block text-slate-600">Entrada</span><b className="text-slate-300">{formatMoney(pos?.entry_price)}</b></div>
                                <div><span className="block text-slate-600">Atual</span><b className="text-cyan-300">{pos?.current_exit_price == null ? "—" : formatMoney(pos.current_exit_price)}</b></div>
                                <div><span className="block text-slate-600">Sacas</span><b className="text-slate-300">{formatNumber(pos?.qty_sacks, 2)}</b></div>
                                <div className="text-right"><span className="block text-slate-600">COFY</span><b className={houseNet >= 0 ? "text-emerald-300" : "text-red-300"}>{houseNet >= 0 ? "+" : "-"}{formatMoney(Math.abs(houseNet))}</b></div>
                              </div>
                            </div>

                            {/* DESKTOP: tabela em uma única linha */}
                            <div className="hidden grid-cols-[76px_minmax(150px,1.4fr)_110px_105px_105px_105px_110px_125px] items-center gap-2 text-[11px] lg:grid">
                              <span className={`w-fit rounded-md px-2 py-1 text-[9px] font-black ${isBuy ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                                {isBuy ? "COMPRA" : "VENDA"}
                              </span>
                              <div className="min-w-0">
                                <div className="truncate font-bold text-slate-200">{userLabel}</div>
                                <div className="text-[9px] text-slate-600">#{pos.id} • {formatDateTime(pos?.created_at)}</div>
                              </div>
                              <b className="text-white">{formatMoney(pos?.amount)}</b>
                              <span className="text-slate-300">{formatMoney(pos?.entry_price)}</span>
                              <span className="text-cyan-300">{pos?.current_exit_price == null ? "—" : formatMoney(pos.current_exit_price)}</span>
                              <span className="text-slate-300">{formatNumber(pos?.qty_sacks, 2)}</span>
                              <b className={clientPl >= 0 ? "text-emerald-300" : "text-red-300"}>{clientPl >= 0 ? "+" : "-"}{formatMoney(Math.abs(clientPl))}</b>
                              <b className={houseNet >= 0 ? "text-emerald-300" : "text-red-300"}>{houseNet >= 0 ? "+" : "-"}{formatMoney(Math.abs(houseNet))}</b>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-3 py-5 text-center text-xs text-slate-500">Nenhuma posição aberta.</div>
                  )}
                </div>

                {/* Centro de Risco COFY */}
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 sm:p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-400">Gestão de contraparte</div>
                      <h3 className="mt-1 text-lg font-black text-white">Risco e resultado COFY</h3>
                      <p className="mt-1 text-xs text-slate-500">Resultado realizado, marcação das abertas e exposição líquida.</p>
                    </div>
                    <div className={`w-fit rounded-full border px-3 py-1 text-[10px] font-black ${dashboard.risk_market_ok ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-amber-500/25 bg-amber-500/10 text-amber-300"}`}>
                      {dashboard.risk_market_ok ? "MTM ATUALIZADO" : "SEM PREÇO PARA MTM"}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
                    <div className={`rounded-2xl border p-3 ${n(dashboard.house_open_mtm) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">P/L aberto COFY</div>
                      <div className={`mt-1 text-lg font-black ${n(dashboard.house_open_mtm) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {n(dashboard.house_open_mtm) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_open_mtm)))}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-600">se liquidasse agora</div>
                    </div>

                    <div className={`rounded-2xl border p-3 ${n(dashboard.house_realized_gross) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Realizado COFY</div>
                      <div className={`mt-1 text-lg font-black ${n(dashboard.house_realized_gross) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {n(dashboard.house_realized_gross) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_realized_gross)))}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-600">ordens encerradas</div>
                    </div>

                    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Spread gerado</div>
                      <div className="mt-1 text-lg font-black text-cyan-300">{formatMoney(dashboard.spread_revenue_total)}</div>
                      <div className="mt-1 text-[10px] text-slate-600">já embutido no P/L</div>
                    </div>

                    <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Comissões parceiros</div>
                      <div className="mt-1 text-lg font-black text-violet-300">- {formatMoney(dashboard.partner_commissions_total)}</div>
                      <div className="mt-1 text-[10px] text-slate-600">obrigação gerada</div>
                    </div>
                  </div>

                  <div className={`mt-3 rounded-2xl border p-3 ${n(dashboard.house_net_result) >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Resultado líquido da casa</div>
                    <div className={`mt-1 text-xl font-black ${n(dashboard.house_net_result) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {n(dashboard.house_net_result) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_net_result)))}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-2 lg:grid-cols-4">
                    <div className="rounded-2xl border border-emerald-500/15 bg-slate-950/60 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Comprados</div>
                      <div className="mt-1 text-base font-black text-white">{formatNumber(dashboard.open_buy_qty, 2)} sacas</div>
                      <div className="mt-1 text-[10px] text-emerald-300">{formatMoney(dashboard.open_buy_total)}</div>
                    </div>
                    <div className="rounded-2xl border border-red-500/15 bg-slate-950/60 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Vendidos</div>
                      <div className="mt-1 text-base font-black text-white">{formatNumber(dashboard.open_sell_qty, 2)} sacas</div>
                      <div className="mt-1 text-[10px] text-red-300">{formatMoney(dashboard.open_sell_total)}</div>
                    </div>
                    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Exposição líquida</div>
                      <div className="mt-1 text-base font-black text-amber-300">{formatNumber(Math.abs(n(dashboard.net_customer_qty)), 2)} sacas</div>
                      <div className="mt-1 text-[10px] text-slate-500">{formatMoney(dashboard.net_exposure_brl)}</div>
                    </div>
                    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Hedge teórico</div>
                      <div className="mt-1 text-base font-black text-cyan-300">
                        {dashboard.hedge_side === "buy" ? "COMPRAR" : dashboard.hedge_side === "sell" ? "VENDER" : "NEUTRO"}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-500">{formatNumber(dashboard.hedge_qty_sacks, 2)} sacas</div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Stress sem hedge</div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div className="rounded-xl bg-slate-900 p-3 text-center"><div className="text-[9px] text-slate-500">1%</div><div className="mt-1 text-sm font-black text-amber-300">{formatMoney(dashboard.risk_shock_1pct_brl)}</div></div>
                      <div className="rounded-xl bg-slate-900 p-3 text-center"><div className="text-[9px] text-slate-500">5%</div><div className="mt-1 text-sm font-black text-orange-300">{formatMoney(dashboard.risk_shock_5pct_brl)}</div></div>
                      <div className="rounded-xl bg-slate-900 p-3 text-center"><div className="text-[9px] text-slate-500">10%</div><div className="mt-1 text-sm font-black text-red-300">{formatMoney(dashboard.risk_shock_10pct_brl)}</div></div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 sm:p-3">
                  <div className="mb-4 flex items-center justify-between gap-2"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Mercado agora</div><h3 className="mt-1 text-lg font-black text-white">Café NY + Dólar</h3></div><div className={`rounded-full border px-3 py-1 text-xs font-black ${mktOk ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-red-500/25 bg-red-500/10 text-red-300"}`}>{mktOk ? "Feed ativo" : "Feed indisponível"}</div></div>
                  {mktOk ? <>
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/65 p-3">
                      <div className="flex items-start justify-between gap-2"><div><div className="text-xs text-slate-500">Café NY • {market.contract || "—"}</div><div className="mt-1 text-xl font-black tracking-tight text-white">{formatNumber(kc, 2)}<span className="ml-1 text-sm text-slate-500">¢/lb</span></div></div><div className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black ${marketChangePct > 0 ? "bg-emerald-500/15 text-emerald-300" : marketChangePct < 0 ? "bg-red-500/15 text-red-300" : "bg-slate-800 text-slate-300"}`}>{marketChangePct > 0 ? <TrendingUp className="h-4 w-4" /> : marketChangePct < 0 ? <TrendingDown className="h-4 w-4" /> : null}{marketChangePct > 0 ? "+" : ""}{formatNumber(marketChangePct, 2)}%</div></div>
                      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-800 pt-3 text-xs"><div className="flex justify-between gap-2"><span className="text-slate-500">Abertura</span><strong className="text-slate-200">{formatNumber(marketOpen, 2)}</strong></div><div className="flex justify-between gap-2"><span className="text-slate-500">Máxima</span><strong className="text-emerald-300">{formatNumber(marketHigh, 2)}</strong></div><div className="flex justify-between gap-2"><span className="text-slate-500">Mínima</span><strong className="text-red-300">{formatNumber(marketLow, 2)}</strong></div><div className="flex justify-between gap-2"><span className="text-slate-500">Fechamento</span><strong className="text-slate-200">{formatNumber(marketClose, 2)}</strong></div></div>
                      <div className="mt-3 grid grid-cols-2 gap-2"><div className="rounded-2xl border border-emerald-500/15 bg-emerald-500/5 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Bid</div><div className="mt-1 text-lg font-black text-emerald-300">{formatNumber(marketBid, 2)}</div></div><div className="rounded-2xl border border-red-500/15 bg-red-500/5 p-3 text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Ask</div><div className="mt-1 text-lg font-black text-red-300">{formatNumber(marketAsk, 2)}</div></div></div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2"><div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-3"><div className="text-[10px] font-black uppercase tracking-wider text-cyan-200/70">Dólar USDT/BRL</div><div className="mt-1 text-xl font-black text-white">R$ {formatNumber(fx, 4)}</div><div className="mt-1 text-[11px] text-slate-500">Cache {cacheFX == null ? "n/a" : `${cacheFX}s`}</div></div><div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-3"><div className="text-[10px] font-black uppercase tracking-wider text-emerald-200/70">Saca referência</div><div className="mt-1 text-xl font-black text-emerald-300">{formatMoney(saca)}</div><div className="mt-1 text-[11px] text-slate-500">KC + dólar</div></div></div>
                  </> : <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300">Sem feed válido no momento.</div>}
                </div>

                <div className="grid gap-2 lg:grid-cols-2">
                  <div className={`rounded-2xl border p-3 sm:p-4 ${pendingWithdrawals.length > 0 ? "border-red-500/25 bg-red-500/5" : "border-slate-800 bg-slate-900"}`}>
                    <div className="flex items-center justify-between gap-2"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Saques</div><h3 className="mt-1 text-lg font-black text-white">Pendentes</h3></div><div className={`rounded-full px-3 py-1 text-xs font-black ${pendingWithdrawals.length > 0 ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}>{pendingWithdrawals.length}</div></div>
                    <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-950/60 p-3"><div className="text-xs text-slate-500">Valor aguardando</div><div className="mt-1 text-xl font-black text-white">{formatMoney(pendingWithdrawalAmount)}</div></div>
                    {pendingWithdrawals.length > 0 ? <div className="mt-3 space-y-2">{pendingWithdrawals.slice(0,5).map(w => <div key={w.id} className="rounded-2xl border border-slate-800 bg-slate-950/55 p-3"><div className="flex items-start justify-between gap-2"><div><div className="text-xs font-bold text-white">Saque #{w.id} • Usuário #{w.user_id}</div><div className="mt-1 text-lg font-black text-red-300">{formatMoney(w.amount)}</div></div><Clock3 className="h-4 w-4 text-amber-300" /></div><div className="mt-2 break-all text-[11px] text-slate-500">PIX: {w.pix_key || "—"}</div><div className="mt-1 text-[11px] text-slate-600">{formatDateTime(w.created_at)}</div></div>)}</div> : <div className="mt-3 text-sm text-slate-500">Nenhum saque aguardando processamento.</div>}
                  </div>

                  <div className={`rounded-2xl border p-3 sm:p-4 ${pendingDeposits.length > 0 ? "border-amber-500/25 bg-amber-500/5" : "border-slate-800 bg-slate-900"}`}>
                    <div className="flex items-center justify-between gap-2"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Depósitos</div><h3 className="mt-1 text-lg font-black text-white">Pendentes</h3></div><div className={`rounded-full px-3 py-1 text-xs font-black ${pendingDeposits.length > 0 ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{pendingDeposits.length}</div></div>
                    <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-950/60 p-3"><div className="text-xs text-slate-500">Valor aguardando</div><div className="mt-1 text-xl font-black text-white">{formatMoney(pendingDepositAmount)}</div></div>
                    {pendingDeposits.length > 0 ? <div className="mt-3 space-y-2">{pendingDeposits.slice(0,5).map(d => <div key={d.id} className="rounded-2xl border border-slate-800 bg-slate-950/55 p-3"><div className="flex items-start justify-between gap-2"><div><div className="text-xs font-bold text-white">Depósito #{d.id} • Usuário #{d.user_id}</div><div className="mt-1 text-lg font-black text-amber-300">{formatMoney(d.amount)}</div></div><Clock3 className="h-4 w-4 text-amber-300" /></div><div className="mt-1 text-[11px] text-slate-600">Status: {d.status || "pending"}</div></div>)}</div> : <div className="mt-3 text-sm text-slate-500">Nenhum depósito aguardando confirmação.</div>}
                  </div>
                </div>

                {(dashboard.house_profit_today !== null || dashboard.portfolio_total !== null) && <div className="grid gap-2 sm:grid-cols-2">{dashboard.house_profit_today !== null && <div className={`rounded-2xl border p-3 ${n(dashboard.house_profit_today) >= 0 ? "border-emerald-500/25 bg-emerald-500/10" : "border-red-500/25 bg-red-500/10"}`}><div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Resultado da casa hoje</div><div className={`mt-2 text-xl font-black ${n(dashboard.house_profit_today) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{n(dashboard.house_profit_today) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_profit_today)))}</div></div>}{dashboard.portfolio_total !== null && <div className="rounded-2xl border border-violet-500/20 bg-violet-500/10 p-3"><div className="text-[10px] font-black uppercase tracking-widest text-violet-300">Patrimônio dos usuários</div><div className="mt-2 text-xl font-black text-white">{formatMoney(dashboard.portfolio_total)}</div></div>}</div>}
              </>
            ) : <div className="rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400">Carregando indicadores...</div>}
          </TabsContent>

          {/* COFY LOCK */}
          <TabsContent value="lock" className="mt-0 space-y-3">
            <div className="rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 via-slate-900 to-slate-950 p-3 sm:p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300">
                    <LockKeyhole className="h-4 w-4" /> COFY LOCK
                  </div>
                  <h2 className="mt-1 text-xl font-black text-white">Administração completa dos contratos</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Datas, hedge, preço protegido, mercado atual, vencimento, auditoria e ações administrativas.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  disabled={lockLoading}
                  onClick={() => fetchLockContracts({ page: lockPage })}
                  className="border-cyan-500/30 text-cyan-200"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${lockLoading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-8">
                {[
                  ["Contratos", formatNumber(lockSummary.total, 0), "text-white"],
                  ["Ativos", formatNumber(lockSummary.active, 0), "text-emerald-300"],
                  ["Assinados", formatNumber(lockSummary.signed, 0), "text-amber-300"],
                  ["Vencidos", formatNumber(lockSummary.matured, 0), "text-orange-300"],
                  ["Liquidados", formatNumber(lockSummary.settled, 0), "text-cyan-300"],
                  ["Cancelados", formatNumber(lockSummary.cancelled, 0), "text-red-300"],
                  ["Protegido", formatMoney(lockSummary.protected_amount), "text-cyan-300"],
                  ["Custos", formatMoney(lockSummary.fee_amount), "text-violet-300"],
                ].map(([label, value, cls]) => (
                  <div key={label} className="rounded-xl border border-slate-800 bg-slate-950/55 p-2.5">
                    <div className="text-[8px] uppercase tracking-wider text-slate-600">{label}</div>
                    <div className={`mt-1 truncate text-sm font-black ${cls}`}>{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <select
                  value={lockStatus}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLockStatus(value);
                    setLockPage(1);
                    setLockExpandedId(null);
                    setLockDetail(null);
                    fetchLockContracts({ page: 1, status: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                >
                  <option value="all">Todos os status</option>
                  <option value="DRAFT">Rascunho</option>
                  <option value="SIGNED">Assinado</option>
                  <option value="ACTIVE">Ativo</option>
                  <option value="MATURED">Vencido</option>
                  <option value="SETTLED">Liquidado</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>

                <select
                  value={lockCoffee}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLockCoffee(value);
                    setLockPage(1);
                    setLockExpandedId(null);
                    setLockDetail(null);
                    fetchLockContracts({ page: 1, coffee: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                >
                  <option value="all">Arábica + Conilon</option>
                  <option value="ARABICA">Arábica</option>
                  <option value="CONILON">Conilon</option>
                </select>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
              <div className="hidden grid-cols-[125px_minmax(150px,1.4fr)_78px_100px_94px_86px_94px_92px_180px] gap-2 border-b border-slate-800 bg-slate-950/75 px-3 py-2 text-[8px] font-black uppercase tracking-wider text-slate-600 lg:grid">
                <span>Contrato</span>
                <span>Cliente</span>
                <span>Café</span>
                <span>Protegido</span>
                <span>Preço</span>
                <span>Sacas</span>
                <span>Custo</span>
                <span>Status</span>
                <span>Ação</span>
              </div>

              {lockLoading && lockContracts.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Carregando COFY LOCK...
                </div>
              ) : lockContracts.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Nenhum contrato COFY LOCK neste filtro.
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {lockContracts.map((c) => {
                    const status = String(c?.status || "").toUpperCase();
                    const active = status === "ACTIVE";
                    const signed = status === "SIGNED";
                    const matured = status === "MATURED";
                    const settled = status === "SETTLED";
                    const cancelled = status === "CANCELLED" || status === "CANCELED";
                    const hedgeConfirmed = String(c?.hedge_status || "").toUpperCase() === "CONFIRMED";
                    const userLabel = c?.user_name || c?.user_email || `Usuário #${c?.user_id ?? "—"}`;
                    const isOpen = Number(lockExpandedId) === Number(c.id);
                    const detail =
                      isOpen &&
                      Number(lockDetail?.contract?.id || 0) === Number(c.id)
                        ? lockDetail
                        : null;

                    const dc = detail?.contract || c;
                    const life = detail?.lifecycle || c?.lifecycle || {};
                    const metrics = detail?.live_metrics || c?.live_metrics || null;
                    const events = Array.isArray(detail?.events) ? detail.events : [];

                    return (
                      <div key={c.id} className="bg-slate-950/30 px-2 py-2 hover:bg-slate-800/30 sm:px-3">
                        {/* MOBILE */}
                        <div className="lg:hidden">
                          <div className="grid grid-cols-[minmax(0,1fr)_74px_92px] items-center gap-1.5">
                            <div className="min-w-0">
                              <div className="truncate text-[10px] font-black text-cyan-200">
                                {c.contract_number || `LOCK #${c.id}`}
                              </div>
                              <div className="truncate text-[10px] font-bold text-slate-300">{userLabel}</div>
                              <div className="text-[8px] text-slate-600">Criado {formatDateTime(c.created_at)}</div>
                            </div>

                            <div>
                              <div className="text-[8px] uppercase text-slate-600">Café</div>
                              <b className={String(c.coffee_type).toUpperCase() === "CONILON" ? "text-amber-300" : "text-cyan-300"}>
                                {String(c.coffee_type).toUpperCase() === "CONILON" ? "CONILON" : "ARÁBICA"}
                              </b>
                            </div>

                            <div className="text-right">
                              <div className="text-[8px] uppercase text-slate-600">Status</div>
                              <b className={lockStatusClass(c.status)}>{lockStatusLabel(c.status)}</b>
                            </div>
                          </div>

                          <div className="mt-1.5 grid grid-cols-4 gap-1 border-t border-slate-800/70 pt-1.5 text-[8px]">
                            <div><span className="block text-slate-600">Protegido</span><b className="text-white">{formatMoney(c.protected_amount)}</b></div>
                            <div><span className="block text-slate-600">Preço</span><b className="text-cyan-300">{formatMoney(c.initial_price_brl_per_bag)}</b></div>
                            <div><span className="block text-slate-600">Sacas</span><b className="text-white">{formatNumber(c.quantity_bags, 2)}</b></div>
                            <div className="text-right"><span className="block text-slate-600">Restam</span><b className="text-emerald-300">{life?.days_remaining == null ? "—" : `${life.days_remaining}d`}</b></div>
                          </div>

                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {signed && !hedgeConfirmed && (
                              <Button
                                size="sm"
                                disabled={lockActionId === Number(c.id)}
                                onClick={() => handleLockAction(c, "confirm_hedge")}
                                className="h-8 bg-cyan-600 px-2 text-[9px] font-black hover:bg-cyan-500"
                              >
                                CONFIRMAR HEDGE
                              </Button>
                            )}

                            {signed && hedgeConfirmed && (
                              <Button
                                size="sm"
                                disabled={lockActionId === Number(c.id)}
                                onClick={() => handleLockAction(c, "activate")}
                                className="h-8 bg-emerald-600 px-2 text-[9px] font-black hover:bg-emerald-500"
                              >
                                ATIVAR LOCK
                              </Button>
                            )}

                            {matured && (
                              <Button
                                type="button"
                                size="sm"
                                disabled={lockActionId === Number(c.id)}
                                onClick={() => handleLockAction(c, "settle")}
                                className="h-8 bg-orange-500 px-2 text-[9px] font-black text-slate-950 hover:bg-orange-400"
                              >
                                LIQUIDAR
                              </Button>
                            )}

                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => toggleLockDetails(c)}
                              className="h-8 border-slate-700 px-2 text-[9px] font-black"
                            >
                              {isOpen ? "FECHAR" : "DETALHES"}
                            </Button>

                            {active && c.maturity_at && (
                              <span className="rounded-md border border-emerald-500/20 bg-emerald-500/5 px-2 py-1.5 text-[9px] font-black text-emerald-300">
                                VENCE {formatDateTime(c.maturity_at)}
                              </span>
                            )}

                            {matured && (
                              <span className="rounded-md border border-orange-500/20 bg-orange-500/5 px-2 py-1.5 text-[9px] font-black text-orange-300">
                                AGUARDANDO LIQUIDAÇÃO
                              </span>
                            )}
                          </div>
                        </div>

                        {/* DESKTOP */}
                        <div className="hidden grid-cols-[125px_minmax(150px,1.4fr)_78px_100px_94px_86px_94px_92px_180px] items-center gap-2 text-[10px] lg:grid">
                          <div>
                            <div className="font-black text-cyan-200">{c.contract_number || `LOCK #${c.id}`}</div>
                            <div className="text-[8px] text-slate-600">{formatDateTime(c.created_at)}</div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate font-bold text-slate-200">{userLabel}</div>
                            <div className="truncate text-[8px] text-slate-600">{c.user_email || `ID ${c.user_id}`}</div>
                          </div>

                          <b className={String(c.coffee_type).toUpperCase() === "CONILON" ? "text-amber-300" : "text-cyan-300"}>
                            {String(c.coffee_type).toUpperCase() === "CONILON" ? "CONILON" : "ARÁBICA"}
                          </b>

                          <b className="text-white">{formatMoney(c.protected_amount)}</b>
                          <span className="text-cyan-300">{formatMoney(c.initial_price_brl_per_bag)}</span>
                          <span className="text-slate-300">{formatNumber(c.quantity_bags, 2)}</span>
                          <span className="text-violet-300">{formatMoney(c.fee_amount)}</span>
                          <b className={lockStatusClass(c.status)}>{lockStatusLabel(c.status)}</b>

                          <div className="flex flex-wrap gap-1">
                            {signed && !hedgeConfirmed && (
                              <Button
                                size="sm"
                                disabled={lockActionId === Number(c.id)}
                                onClick={() => handleLockAction(c, "confirm_hedge")}
                                className="h-7 bg-cyan-600 px-2 text-[8px] font-black hover:bg-cyan-500"
                              >
                                HEDGE
                              </Button>
                            )}

                            {signed && hedgeConfirmed && (
                              <Button
                                size="sm"
                                disabled={lockActionId === Number(c.id)}
                                onClick={() => handleLockAction(c, "activate")}
                                className="h-7 bg-emerald-600 px-2 text-[8px] font-black hover:bg-emerald-500"
                              >
                                ATIVAR
                              </Button>
                            )}

                            {matured && (
                              <Button
                                type="button"
                                size="sm"
                                disabled={lockActionId === Number(c.id)}
                                onClick={() => handleLockAction(c, "settle")}
                                className="h-7 bg-orange-500 px-2 text-[8px] font-black text-slate-950 hover:bg-orange-400"
                              >
                                LIQUIDAR
                              </Button>
                            )}

                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => toggleLockDetails(c)}
                              className="h-7 border-slate-700 px-2 text-[8px] font-black"
                            >
                              {isOpen ? "FECHAR" : "DETALHES"}
                            </Button>
                          </div>
                        </div>

                        {/* FICHA COMPLETA */}
                        {isOpen && (
                          <div className="mt-2 overflow-hidden rounded-xl border border-cyan-500/15 bg-slate-950/75">
                            {lockDetailLoading && !detail ? (
                              <div className="p-6 text-center text-xs text-slate-500">
                                <RefreshCw className="mx-auto mb-2 h-4 w-4 animate-spin" />
                                Carregando ficha completa...
                              </div>
                            ) : (
                              <div className="space-y-3 p-2.5 sm:p-4">
                                <div className="flex flex-col gap-2 border-b border-slate-800 pb-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div>
                                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-cyan-400">Ficha completa</div>
                                    <div className="text-base font-black text-white">{dc.contract_number || `LOCK #${dc.id}`}</div>
                                    <div className="text-[10px] text-slate-500">
                                      Cliente: {detail?.user?.name || c.user_name || "—"} • {detail?.user?.email || c.user_email || "—"}
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap gap-1.5">
                                    <span className={`rounded-full border border-slate-700 bg-slate-900 px-2 py-1 text-[9px] font-black ${lockStatusClass(dc.status)}`}>
                                      {lockStatusLabel(dc.status)}
                                    </span>
                                    <span className="rounded-full border border-slate-700 bg-slate-900 px-2 py-1 text-[9px] font-black text-slate-300">
                                      Hedge: {dc.hedge_status || "—"}
                                    </span>
                                    {detail && (
                                      <span
                                        className={`rounded-full border px-2 py-1 text-[9px] font-black ${
                                          detail?.reservation
                                            ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-300"
                                            : "border-red-500/25 bg-red-500/5 text-red-300"
                                        }`}
                                      >
                                        {detail?.reservation ? "RESERVA REGISTRADA" : "SEM RESERVA"}
                                      </span>
                                    )}
                                    {life?.days_remaining != null && (
                                      <span className="rounded-full border border-emerald-500/20 bg-emerald-500/5 px-2 py-1 text-[9px] font-black text-emerald-300">
                                        {life.days_remaining} dia(s) restante(s)
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                                  {[
                                    ["Plano", dc.plan_name || detail?.plan?.name || "—"],
                                    ["Café", String(dc.coffee_type || "—").toUpperCase()],
                                    ["Prazo original", `${Number(dc.duration_days || 0)} dias`],
                                    ["Sacas", formatNumber(dc.quantity_bags, 4)],
                                    ["Valor protegido", formatMoney(dc.protected_amount)],
                                    ["Custo", formatMoney(dc.fee_amount)],
                                    ["Preço protegido", formatMoney(dc.initial_price_brl_per_bag)],
                                    ["Preço atual", metrics ? formatMoney(metrics.current_price_brl_per_bag) : "—"],
                                    ["Dif./saca", metrics ? formatMoney(metrics.difference_brl_per_bag) : "—"],
                                    ["Variação", metrics ? `${formatNumber(metrics.variation_pct, 2)}%` : "—"],
                                    ["Proteção estimada", metrics ? formatMoney(metrics.protection_amount) : "—"],
                                    ["Valorização", metrics ? formatMoney(metrics.upside_amount) : "—"],
                                  ].map(([label, value]) => (
                                    <div key={label} className="rounded-lg border border-slate-800 bg-slate-900/60 p-2">
                                      <div className="text-[8px] uppercase text-slate-600">{label}</div>
                                      <div className="mt-1 break-words text-[10px] font-black text-slate-200">{value}</div>
                                    </div>
                                  ))}
                                </div>

                                {detail && (
                                  <div
                                    className={`rounded-xl border p-3 ${
                                      detail?.reservation
                                        ? "border-emerald-500/20 bg-emerald-500/5"
                                        : "border-red-500/25 bg-red-500/5"
                                    }`}
                                  >
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                      <div>
                                        <div
                                          className={`text-[9px] font-black uppercase tracking-wider ${
                                            detail?.reservation ? "text-emerald-300" : "text-red-300"
                                          }`}
                                        >
                                          Reserva financeira
                                        </div>
                                        <div className="mt-1 text-[10px] text-slate-400">
                                          {detail?.reservation
                                            ? "O capital deste LOCK foi reservado na carteira do cliente antes do hedge."
                                            : "Este contrato não possui reserva financeira válida. Hedge e ativação devem permanecer bloqueados."}
                                        </div>
                                      </div>

                                      <div className="text-left sm:text-right">
                                        <div className="text-[8px] uppercase text-slate-600">Valor da reserva</div>
                                        <div
                                          className={`text-base font-black ${
                                            detail?.reservation ? "text-emerald-300" : "text-red-300"
                                          }`}
                                        >
                                          {detail?.reservation
                                            ? formatMoney(detail.reservation.amount)
                                            : "SEM RESERVA"}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                                      {[
                                        [
                                          "Saldo antes",
                                          detail?.reservation?.balance_before == null
                                            ? "—"
                                            : formatMoney(detail.reservation.balance_before),
                                        ],
                                        [
                                          "Saldo após reserva",
                                          detail?.reservation?.balance_after == null
                                            ? "—"
                                            : formatMoney(detail.reservation.balance_after),
                                        ],
                                        [
                                          "Limite efetivo",
                                          detail?.lock_policy
                                            ? formatMoney(detail.lock_policy.effective_limit)
                                            : "—",
                                        ],
                                        [
                                          "Limite disponível",
                                          detail?.lock_policy
                                            ? formatMoney(detail.lock_policy.available_limit)
                                            : "—",
                                        ],
                                      ].map(([label, value]) => (
                                        <div key={label} className="rounded-lg border border-slate-800 bg-slate-950/55 p-2">
                                          <div className="text-[8px] uppercase text-slate-600">{label}</div>
                                          <div className="mt-1 break-words text-[10px] font-black text-slate-200">{value}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {metrics && (
                                  <div className="rounded-xl border border-slate-800 bg-slate-900/55 p-3">
                                    <div className="flex items-center justify-between gap-2">
                                      <div>
                                        <div className="text-[9px] font-black uppercase tracking-wider text-emerald-400">Acompanhamento ao vivo</div>
                                        <div className="mt-1 text-xs text-slate-400">
                                          {metrics.benefit_mode === "PROTECTION"
                                            ? `A proteção está compensando uma queda estimada de ${formatMoney(metrics.protection_amount)}.`
                                            : metrics.benefit_mode === "UPSIDE"
                                            ? `A referência está acima do piso, com valorização estimada de ${formatMoney(metrics.upside_amount)}.`
                                            : "A referência está próxima do preço protegido."}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-[8px] uppercase text-slate-600">Referência atual</div>
                                        <div className="text-lg font-black text-cyan-300">{formatMoney(metrics.current_price_brl_per_bag)}</div>
                                      </div>
                                    </div>

                                    {metrics.progress_pct != null && (
                                      <div className="mt-3">
                                        <div className="mb-1 flex justify-between text-[8px] text-slate-600">
                                          <span>Início {formatDateTime(dc.started_at)}</span>
                                          <span>{formatNumber(metrics.progress_pct, 1)}%</span>
                                          <span>Vencimento {formatDateTime(dc.maturity_at)}</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                                          <div
                                            className="h-full rounded-full bg-emerald-400 transition-all"
                                            style={{ width: `${Math.max(0, Math.min(100, Number(metrics.progress_pct) || 0))}%` }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="grid gap-2 lg:grid-cols-2">
                                  <div className="rounded-xl border border-slate-800 bg-slate-900/55 p-3">
                                    <div className="mb-2 text-[9px] font-black uppercase tracking-wider text-cyan-400">Datas do contrato</div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[9px]">
                                      {[
                                        ["Criado", dc.created_at],
                                        ["Cotado", dc.quoted_at],
                                        ["Assinado", dc.signed_at],
                                        ["Hedge confirmado", dc.hedge_confirmed_at],
                                        ["Ativado", dc.activated_at],
                                        ["Início", dc.started_at],
                                        ["Vencimento", dc.maturity_at],
                                        ["Liquidado", dc.settled_at],
                                        ["Última atualização", dc.updated_at],
                                      ].map(([label, value]) => (
                                        <div key={label}>
                                          <div className="text-slate-600">{label}</div>
                                          <div className="font-bold text-slate-300">{formatDateTime(value)}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="rounded-xl border border-slate-800 bg-slate-900/55 p-3">
                                    <div className="mb-2 text-[9px] font-black uppercase tracking-wider text-violet-400">Referências e auditoria</div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[9px]">
                                      <div><span className="block text-slate-600">Benchmark</span><b className="text-slate-300">{dc.benchmark_market || "—"}</b></div>
                                      <div><span className="block text-slate-600">Contrato bolsa</span><b className="text-slate-300">{dc.benchmark_symbol || "—"}</b></div>
                                      <div><span className="block text-slate-600">USD/BRL abertura</span><b className="text-slate-300">{dc.usd_brl_at_open == null ? "—" : formatNumber(dc.usd_brl_at_open, 4)}</b></div>
                                      <div><span className="block text-slate-600">Modo pedido</span><b className="text-slate-300">{dc.request_mode || "—"}</b></div>
                                      <div className="col-span-2"><span className="block text-slate-600">Referência hedge</span><b className="break-all text-slate-300">{dc.hedge_reference || "—"}</b></div>
                                      <div className="col-span-2"><span className="block text-slate-600">Observação hedge</span><b className="break-words text-slate-300">{dc.hedge_note || "—"}</b></div>
                                      <div className="col-span-2"><span className="block text-slate-600">Hash do contrato</span><b className="break-all text-[8px] text-slate-400">{dc.contract_hash || "—"}</b></div>
                                    </div>
                                  </div>
                                </div>

                                {String(dc?.status || "").toUpperCase() === "MATURED" && !dc?.settled_at && (
                                  <div className="rounded-xl border border-orange-500/25 bg-orange-500/5 p-3">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                      <div>
                                        <div className="text-[9px] font-black uppercase tracking-wider text-orange-300">Liquidação disponível</div>
                                        <p className="mt-1 text-[10px] text-slate-400">
                                          A liquidação usa a cotação atual, preserva o capital protegido na queda, soma a valorização na alta e desconta o spread antes do crédito.
                                        </p>
                                      </div>
                                      <Button
                                        type="button"
                                        disabled={lockActionId === Number(c.id)}
                                        onClick={() => handleLockAction(dc, "settle")}
                                        className="shrink-0 bg-orange-500 font-black text-slate-950 hover:bg-orange-400"
                                      >
                                        {lockActionId === Number(c.id) ? "LIQUIDANDO..." : "LIQUIDAR CONTRATO"}
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                {(life?.can_change_maturity || active || matured) && !settled && !cancelled && (
                                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                                    <div className="text-[9px] font-black uppercase tracking-wider text-amber-300">Controle administrativo do vencimento</div>
                                    <p className="mt-1 text-[10px] text-slate-500">
                                      Use para antecipar testes ou prorrogar o LOCK. Toda alteração fica registrada no histórico.
                                    </p>

                                    <div className="mt-3 grid gap-2 lg:grid-cols-[220px_minmax(0,1fr)_auto]">
                                      <Input
                                        type="datetime-local"
                                        step="1"
                                        value={lockMaturityInput}
                                        onChange={(e) => setLockMaturityInput(e.target.value)}
                                        className="border-slate-700 bg-slate-950 text-white"
                                      />
                                      <Input
                                        value={lockMaturityReason}
                                        onChange={(e) => setLockMaturityReason(e.target.value)}
                                        placeholder="Motivo da alteração — ex.: teste de vencimento"
                                        maxLength={1000}
                                        className="border-slate-700 bg-slate-950 text-white"
                                      />
                                      <Button
                                        type="button"
                                        disabled={lockActionId === Number(c.id) || !lockMaturityInput}
                                        onClick={() => handleLockAction(dc, "change_maturity")}
                                        className="bg-amber-500 font-black text-slate-950 hover:bg-amber-400"
                                      >
                                        SALVAR DATA
                                      </Button>
                                    </div>

                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setLockMaturityInput(lockNowInputValue());
                                          setLockMaturityReason("Teste administrativo de vencimento imediato.");
                                        }}
                                        className="border-orange-500/30 text-orange-300"
                                      >
                                        VENCER AGORA PARA TESTE
                                      </Button>

                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setLockMaturityInput(lockShiftDateInput(dc.maturity_at || Date.now(), 30));
                                          setLockMaturityReason("Prorrogação administrativa de 30 dias.");
                                        }}
                                        className="border-cyan-500/30 text-cyan-300"
                                      >
                                        +30 DIAS
                                      </Button>

                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setLockMaturityInput(lockShiftDateInput(dc.maturity_at || Date.now(), 90));
                                          setLockMaturityReason("Prorrogação administrativa de 90 dias.");
                                        }}
                                        className="border-cyan-500/30 text-cyan-300"
                                      >
                                        +90 DIAS
                                      </Button>

                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={lockActionId === Number(c.id)}
                                        onClick={() => handleLockAction(dc, "sync_maturity")}
                                        className="border-slate-700 text-slate-300"
                                      >
                                        SINCRONIZAR STATUS
                                      </Button>

                                      {life?.can_cancel !== false && !cancelled && !settled && (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          disabled={lockActionId === Number(c.id)}
                                          onClick={() => handleLockAction(dc, "cancel")}
                                          className="border-red-500/30 text-red-300 hover:text-red-200"
                                        >
                                          CANCELAR CONTRATO
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <div className="rounded-xl border border-slate-800 bg-slate-900/55 p-3">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                                      Histórico / eventos
                                    </div>
                                    <span className="text-[9px] text-slate-600">{events.length} registro(s)</span>
                                  </div>

                                  {events.length ? (
                                    <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto pr-1">
                                      {events.map((ev) => (
                                        <div key={ev.id} className="rounded-lg border border-slate-800 bg-slate-950/60 p-2">
                                          <div className="flex flex-wrap items-center justify-between gap-1">
                                            <b className="text-[9px] text-cyan-300">{ev.event_type || "EVENTO"}</b>
                                            <span className="text-[8px] text-slate-600">{formatDateTime(ev.created_at)}</span>
                                          </div>
                                          <div className="mt-1 text-[9px] text-slate-300">{ev.message || "—"}</div>
                                          <div className="mt-1 text-[8px] text-slate-600">
                                            {ev.actor_type || "SYSTEM"}{ev.actor_id ? ` #${ev.actor_id}` : ""}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="mt-2 text-[10px] text-slate-600">
                                      {lockDetailLoading ? "Carregando histórico..." : "Nenhum evento registrado."}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900 p-2.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={lockLoading || lockPage <= 1}
                onClick={() => {
                  const next = lockPage - 1;
                  setLockPage(next);
                  setLockExpandedId(null);
                  setLockDetail(null);
                  fetchLockContracts({ page: next });
                }}
                className="border-slate-700"
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Anterior
              </Button>

              <div className="text-xs text-slate-500">Página {lockPage} de {lockTotalPages}</div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={lockLoading || lockPage >= lockTotalPages}
                onClick={() => {
                  const next = lockPage + 1;
                  setLockPage(next);
                  setLockExpandedId(null);
                  setLockDetail(null);
                  fetchLockContracts({ page: next });
                }}
                className="border-slate-700"
              >
                Próxima <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          {/* WhatsApp */}
          <TabsContent value="whatsapp" className="mt-0 space-y-3 overflow-x-hidden">
            <div className={`rounded-2xl border border-slate-800 bg-slate-900 p-3 sm:p-4 ${waSelected ? "hidden lg:block" : "block"}`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-400">
                    <MessageCircle className="h-4 w-4 shrink-0" /> Atendimento WhatsApp
                  </div>
                  <h2 className="mt-1 text-xl font-black text-white">Conversas COFY</h2>
                  <p className="mt-1 text-xs text-slate-500">Acompanhe cliente, IA e atendimento humano em uma única tela.</p>
                </div>
                <Button variant="outline" className="w-full border-slate-700 sm:w-auto" onClick={fetchWhatsAppConversations} disabled={waLoading}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${waLoading ? "animate-spin" : ""}`} /> Atualizar
                </Button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
                {[
                  ["Conversas", waStats.total, "text-white"],
                  ["Na IA", waStats.ai, "text-emerald-300"],
                  ["Aguardando humano", waStats.waiting_human, "text-amber-300"],
                  ["Com humano", waStats.human, "text-cyan-300"],
                ].map(([label, value, cls]) => (
                  <div key={label} className="min-w-0 rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                    <div className="break-words text-[9px] font-black uppercase tracking-wider text-slate-500">{label}</div>
                    <div className={`mt-1 text-xl font-black ${cls}`}>{value ?? 0}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 lg:grid lg:min-h-[620px] lg:grid-cols-[340px_minmax(0,1fr)]">
              {/* Lista: no celular some quando uma conversa é aberta */}
              <div className={`${waSelected ? "hidden lg:block" : "block"} min-w-0 border-slate-800 lg:border-r`}>
                <div className="border-b border-slate-800 p-3 text-xs font-black uppercase tracking-wider text-slate-500">
                  Conversas recentes
                </div>
                <div className="max-h-[calc(100dvh-260px)] min-h-[420px] overflow-y-auto overflow-x-hidden lg:max-h-[620px] lg:min-h-0">
                  {waConversations.length ? waConversations.map((c) => {
                    const active = Number(waSelected?.id) === Number(c.id);
                    const mode = c.mode || "ai";
                    return (
                      <button key={c.id} type="button" onClick={() => selectWhatsAppConversation(c)}
                        className={`block w-full min-w-0 overflow-hidden border-b border-slate-800 p-3 text-left transition ${active ? "bg-emerald-500/10" : "hover:bg-slate-950/60"}`}>
                        <div className="flex min-w-0 items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-black text-white">{c.contact_name || "Cliente WhatsApp"}</div>
                            <div className="mt-1 truncate text-[11px] text-slate-500">{c.phone_e164 || "—"}</div>
                          </div>
                          <span className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-black uppercase ${
                            mode === "waiting_human" ? "bg-amber-500/15 text-amber-300" :
                            mode === "human" ? "bg-cyan-500/15 text-cyan-300" :
                            "bg-emerald-500/15 text-emerald-300"
                          }`}>{mode === "waiting_human" ? "Aguardando" : mode === "human" ? "Humano" : "IA"}</span>
                        </div>
                        <div className="mt-2 truncate text-xs text-slate-400">{c.last_message || c.last_message_text || "Sem prévia"}</div>
                        <div className="mt-1 text-[10px] text-slate-600">{formatDateTime(c.updated_at || c.last_message_at)}</div>
                      </button>
                    );
                  }) : (
                    <div className="p-6 text-center text-sm text-slate-500">{waLoading ? "Carregando..." : "Nenhuma conversa."}</div>
                  )}
                </div>
              </div>

              {/* Conversa: no celular vira a tela principal */}
              <div className={`${waSelected ? "flex" : "hidden lg:flex"} min-w-0 w-full flex-col overflow-hidden lg:min-h-[620px]`}>
                {waSelected ? (
                  <>
                    <div className="shrink-0 border-b border-slate-800 bg-slate-900 p-2.5 sm:p-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => { setWaSelected(null); setWaMessages([]); }}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-950 text-slate-200 active:scale-95 lg:hidden"
                          aria-label="Voltar para conversas"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>

                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-black text-white sm:text-base">{waSelected.contact_name || "Cliente WhatsApp"}</div>
                          <div className="truncate text-[11px] text-slate-500 sm:text-xs">{waSelected.phone_e164}</div>
                        </div>

                        <span className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-black uppercase ${
                          waSelected.mode === "waiting_human" ? "bg-amber-500/15 text-amber-300" :
                          waSelected.mode === "human" ? "bg-cyan-500/15 text-cyan-300" :
                          "bg-emerald-500/15 text-emerald-300"
                        }`}>
                          {waSelected.mode === "waiting_human" ? "Aguardando" : waSelected.mode === "human" ? "Humano" : "IA"}
                        </span>
                      </div>

                      <div className="mt-2 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                        {waSelected.mode !== "human" && (
                          <Button onClick={takeWhatsAppConversation} className="w-full bg-cyan-500 font-black text-slate-950 hover:bg-cyan-400 sm:w-auto">
                            Assumir atendimento
                          </Button>
                        )}
                        {waSelected.mode !== "ai" && (
                          <Button variant="outline" onClick={returnWhatsAppToAI} className="w-full border-emerald-500/30 text-emerald-300 sm:w-auto">
                            Devolver para IA
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex min-h-[360px] flex-1 flex-col space-y-3 overflow-y-auto overflow-x-hidden bg-slate-950/40 p-2.5 sm:min-h-0 sm:p-3 lg:max-h-[500px]">
                      {waMessages.length ? waMessages.map((m) => {
                        const customer = m.direction === "inbound" || m.sender_type === "customer";
                        const who = m.sender_type === "human" ? "Atendente" : m.sender_type === "ai" ? "COFY IA" : m.sender_type === "system" ? "Sistema" : "Cliente";
                        return (
                          <div key={m.id} className={`flex w-full min-w-0 ${customer ? "justify-start" : "justify-end"}`}>
                            <div className={`min-w-0 max-w-[88%] overflow-hidden rounded-2xl border px-3 py-2 sm:max-w-[78%] ${
                              customer ? "border-slate-700 bg-slate-800 text-slate-100" :
                              m.sender_type === "human" ? "border-cyan-500/25 bg-cyan-500/10 text-cyan-50" :
                              "border-emerald-500/25 bg-emerald-500/10 text-emerald-50"
                            }`}>
                              <div className="mb-1 text-[9px] font-black uppercase tracking-wider opacity-60">{who}</div>
                              <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-relaxed">{m.message_text || "—"}</div>
                              <div className="mt-2 break-words text-[9px] opacity-50">{formatDateTime(m.created_at)}{m.message_status ? ` • ${m.message_status}` : ""}</div>
                            </div>
                          </div>
                        );
                      }) : <div className="py-16 text-center text-sm text-slate-600">Nenhuma mensagem registrada.</div>}
                    </div>

                    {waSelected.mode === "human" ? (
                      <form onSubmit={sendWhatsAppHumanMessage} className="shrink-0 border-t border-slate-800 bg-slate-900 p-2.5 sm:p-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <Input
                            value={waDraft}
                            onChange={(e) => setWaDraft(e.target.value)}
                            placeholder="Digite sua mensagem..."
                            maxLength={4000}
                            disabled={waSending}
                            className="min-w-0 flex-1 border-slate-700 bg-slate-950 text-white"
                          />
                          <Button
                            type="submit"
                            disabled={waSending || !String(waDraft || "").trim()}
                            className="shrink-0 bg-cyan-500 px-3 font-black text-slate-950 hover:bg-cyan-400 sm:px-4"
                          >
                            {waSending ? "..." : "Enviar"}
                          </Button>
                        </div>
                        <div className="mt-2 text-[10px] text-slate-600">A mensagem será enviada pelo número oficial da COFY.</div>
                      </form>
                    ) : (
                      <div className="shrink-0 border-t border-slate-800 bg-slate-900 p-3 text-center text-xs text-slate-500">
                        {waSelected.mode === "waiting_human"
                          ? "Cliente aguardando atendimento humano. Toque em Assumir atendimento para responder."
                          : "COFY IA está respondendo automaticamente."}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-1 items-center justify-center p-8 text-center">
                    <div>
                      <MessageCircle className="mx-auto h-10 w-10 text-slate-700" />
                      <div className="mt-3 font-black text-slate-300">Selecione uma conversa</div>
                      <div className="mt-1 text-xs text-slate-600">O histórico completo aparecerá aqui.</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            {settings && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Configurações da Plataforma</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Asaas PIX</h3>
                    <div className="grid gap-2">
                      <div className="space-y-2">
                        <Label className="text-slate-300">API Key</Label>
                        <Input
                          type="password"
                          placeholder="Asaas API Key"
                          value={settings.asaas_api_key || ""}
                          onChange={(e) => setSettings({ ...settings, asaas_api_key: e.target.value })}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Ambiente</Label>
                        <select
                          value={settings.asaas_environment}
                          onChange={(e) => setSettings({ ...settings, asaas_environment: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                        >
                          <option value="sandbox">Sandbox</option>
                          <option value="production">Produção</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-slate-800">
                    <h3 className="text-lg font-semibold text-white">Google OAuth</h3>
                    <div className="grid gap-2">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Client ID</Label>
                        <Input
                          placeholder="Google Client ID"
                          value={settings.google_client_id || ""}
                          onChange={(e) => setSettings({ ...settings, google_client_id: e.target.value })}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Client Secret</Label>
                        <Input
                          type="password"
                          placeholder="Google Client Secret"
                          value={settings.google_client_secret || ""}
                          onChange={(e) => setSettings({ ...settings, google_client_secret: e.target.value })}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveSettings}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    Salvar Configurações
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contracts */}
          <TabsContent value="contracts">
            <div className="space-y-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Criar Novo Contrato</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-2">
                    <Input
                      placeholder="Símbolo (ex: KCZ5)"
                      value={newContract.symbol}
                      onChange={(e) => setNewContract({ ...newContract, symbol: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <Input
                      placeholder="Nome"
                      value={newContract.name}
                      onChange={(e) => setNewContract({ ...newContract, name: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="Spread %"
                      value={newContract.spread}
                      onChange={(e) => setNewContract({ ...newContract, spread: parseFloat(e.target.value) })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <Button onClick={handleCreateContract} className="bg-gradient-to-r from-emerald-500 to-cyan-500">
                      <Plus className="w-4 h-4 mr-2" />
                      Criar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Contratos Cadastrados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {contracts.map((contract) => (
                      <div key={contract.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        {editingContract?.id === contract.id ? (
                          <div className="flex-1 grid md:grid-cols-4 gap-2">
                            <Input
                              value={editingContract.symbol}
                              onChange={(e) => setEditingContract({ ...editingContract, symbol: e.target.value })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <Input
                              value={editingContract.name}
                              onChange={(e) => setEditingContract({ ...editingContract, name: e.target.value })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <Input
                              type="number"
                              step="0.1"
                              value={editingContract.spread}
                              onChange={(e) => setEditingContract({ ...editingContract, spread: parseFloat(e.target.value) })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleUpdateContract(editingContract)} className="bg-emerald-500 hover:bg-emerald-600">
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingContract(null)} className="border-slate-700">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex-1 grid md:grid-cols-3 gap-2">
                              <div>
                                <div className="text-sm text-slate-400">Símbolo</div>
                                <div className="text-white font-semibold">{contract.symbol}</div>
                              </div>
                              <div>
                                <div className="text-sm text-slate-400">Nome</div>
                                <div className="text-white">{contract.name}</div>
                              </div>
                              <div>
                                <div className="text-sm text-slate-400">Spread</div>
                                <div className="text-white">{contract.spread}%</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={!!contract.active}
                                onCheckedChange={(checked) => handleUpdateContract({ ...contract, active: checked })}
                              />
                              <Button size="sm" variant="outline" onClick={() => setEditingContract(contract)} className="border-slate-700">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteContract(contract.id)}
                                className="border-slate-700 text-red-400 hover:text-red-300"
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Deposits */}
          <TabsContent value="deposits">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Depósitos Pendentes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pendingDeposits.map((deposit) => (
                    <div key={deposit.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                      <div className="flex-1 grid md:grid-cols-4 gap-2">
                        <div>
                          <div className="text-sm text-slate-400">Usuário</div>
                          <div className="text-white">{deposit.user_id}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Valor</div>
                          <div className="text-white font-semibold">{formatMoney(deposit.amount)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Código PIX</div>
                          <div className="text-white font-mono text-sm break-all">{deposit.pix_code}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Status</div>
                          <div className="text-yellow-400">{deposit.status}</div>
                        </div>
                      </div>
                      <Button onClick={() => handleConfirmDeposit(deposit.id)} className="bg-emerald-500 hover:bg-emerald-600">
                        Confirmar
                      </Button>
                    </div>
                  ))}
                  {pendingDeposits.length === 0 && (
                    <div className="text-center text-slate-400 py-8">Nenhum depósito pendente</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals */}
          <TabsContent value="withdrawals">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Saques</span>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button variant={withdrawStatus === "pending" ? "default" : "outline"} onClick={() => fetchWithdrawals("pending")}>
                      Pendentes
                    </Button>
                    <Button variant={withdrawStatus === "paid" ? "default" : "outline"} onClick={() => fetchWithdrawals("paid")}>
                      Pagos
                    </Button>
                    <Button variant={withdrawStatus === "rejected" ? "default" : "outline"} onClick={() => fetchWithdrawals("rejected")}>
                      Recusados
                    </Button>
                    <Button variant={withdrawStatus === "all" ? "default" : "outline"} onClick={() => fetchWithdrawals("all")}>
                      Todos
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(withdrawals || []).map((w) => (
                    <div key={w.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                      <div className="flex-1 grid md:grid-cols-5 gap-2">
                        <div>
                          <div className="text-sm text-slate-400">ID</div>
                          <div className="text-white">{w.id}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Usuário</div>
                          <div className="text-white">{w.user_id}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Valor</div>
                          <div className="text-white font-semibold">{formatMoney(w.amount)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">PIX</div>
                          <div className="text-white text-sm break-all">{w.pix_key || "—"}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Criado</div>
                          <div className="text-white text-sm">{formatDateTime(w.created_at)}</div>
                        </div>
                      </div>

                      {w.status === "pending" ? (
                        <div className="flex gap-2">
                          <Button onClick={() => handleConfirmWithdrawal(w.id)} className="bg-emerald-500 hover:bg-emerald-600">
                            <Check className="w-4 h-4 mr-2" />
                            Confirmar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleRejectWithdrawal(w.id)}
                            className="border-slate-700 text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Recusar
                          </Button>
                        </div>
                      ) : (
                        <div className="text-slate-400 text-sm">
                          Status: <span className="text-white">{w.status}</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {(!withdrawals || withdrawals.length === 0) && (
                    <div className="text-center text-slate-400 py-8">Nenhum saque encontrado</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Histórico de Negociações */}
          <TabsContent value="trades" className="mt-0 space-y-3">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 sm:p-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400">Auditoria</div>
                  <h2 className="mt-1 text-xl font-black text-white">Histórico de negociações</h2>
                  <p className="mt-1 text-xs text-slate-500">P/L do cliente e espelho da COFY operação por operação.</p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  disabled={tradesLoading}
                  onClick={() => fetchTrades({ page: tradesPage })}
                  className="border-slate-700"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${tradesLoading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <select
                  value={tradesPeriod}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradesPeriod(value);
                    setTradesPage(1);
                    fetchTrades({ page: 1, period: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                >
                  <option value="today">Hoje</option>
                  <option value="7d">Últimos 7 dias</option>
                  <option value="30d">Últimos 30 dias</option>
                  <option value="all">Todo período</option>
                </select>

                <select
                  value={tradesStatus}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradesStatus(value);
                    setTradesPage(1);
                    fetchTrades({ page: 1, status: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                >
                  <option value="all">Todos os status</option>
                  <option value="open">Abertas</option>
                  <option value="closed">Fechadas</option>
                  <option value="canceled">Canceladas</option>
                </select>

                <select
                  value={tradesSide}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradesSide(value);
                    setTradesPage(1);
                    fetchTrades({ page: 1, side: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                >
                  <option value="all">Compra e venda</option>
                  <option value="buy">Compras</option>
                  <option value="sell">Vendas</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Volume</div>
                <div className="mt-1 text-base font-black text-white">{formatMoney(tradesSummary.volume)}</div>
              </div>
              <div className={`rounded-2xl border p-3 ${n(tradesSummary.client_pl) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">P/L clientes</div>
                <div className={`mt-1 text-base font-black ${n(tradesSummary.client_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(tradesSummary.client_pl)}</div>
              </div>
              <div className={`rounded-2xl border p-3 ${n(tradesSummary.house_gross_pl) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">COFY bruto</div>
                <div className={`mt-1 text-base font-black ${n(tradesSummary.house_gross_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(tradesSummary.house_gross_pl)}</div>
              </div>
              <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Parceiros</div>
                <div className="mt-1 text-base font-black text-violet-300">- {formatMoney(tradesSummary.partner_commission)}</div>
              </div>
              <div className={`col-span-2 rounded-2xl border p-3 lg:col-span-1 ${n(tradesSummary.house_net_pl) >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">COFY líquido</div>
                <div className={`mt-1 text-xl font-black ${n(tradesSummary.house_net_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(tradesSummary.house_net_pl)}</div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
              <div className="hidden grid-cols-[76px_minmax(150px,1.4fr)_90px_105px_105px_85px_105px_115px_76px] gap-2 border-b border-slate-800 bg-slate-950/75 px-3 py-2 text-[8px] font-black uppercase tracking-wider text-slate-600 lg:grid">
                <span>Tipo</span>
                <span>Cliente</span>
                <span>Valor</span>
                <span>Entrada</span>
                <span>Saída/Atual</span>
                <span>Sacas</span>
                <span>P/L cliente</span>
                <span>COFY líquido</span>
                <span>Status</span>
              </div>

              {tradesLoading && trades.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Carregando negociações...
                </div>
              ) : trades.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  Nenhuma negociação encontrada neste filtro.
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {trades.map((trade) => {
                    const isBuy = String(trade?.side || "").toLowerCase() === "buy";
                    const clientPl = n(trade?.client_pl);
                    const houseNet = n(trade?.house_net_pl);
                    const userLabel =
                      trade?.user_name ||
                      trade?.user_email ||
                      `Usuário #${trade?.user_id ?? "—"}`;
                    const statusLabel =
                      trade?.status === "open"
                        ? "ABERTA"
                        : trade?.status === "closed"
                        ? "FECHADA"
                        : "CANCELADA";
                    const exitPrice =
                      trade?.status === "open"
                        ? trade?.current_exit_price
                        : trade?.close_price;

                    return (
                      <div key={trade.id} className="bg-slate-950/30 px-2 py-2 hover:bg-slate-800/30 sm:px-3">
                        {/* MOBILE / TABLET • duas linhas slim, sem scroll horizontal */}
                        <div className="lg:hidden">
                          <div className="grid grid-cols-[58px_minmax(0,1fr)_86px_82px] items-center gap-1.5">
                            <span className={`rounded-md px-1.5 py-1 text-center text-[9px] font-black ${
                              isBuy
                                ? "bg-emerald-500/15 text-emerald-300"
                                : "bg-red-500/15 text-red-300"
                            }`}>
                              {isBuy ? "COMPRA" : "VENDA"}
                            </span>

                            <div className="min-w-0">
                              <div className="truncate text-[11px] font-bold text-slate-200">{userLabel}</div>
                              <div className="truncate text-[8px] text-slate-600">
                                #{trade.id} • {formatDateTime(trade.created_at)}
                              </div>
                            </div>

                            <div className="text-right">
                              <div className="text-[8px] uppercase text-slate-600">Valor</div>
                              <div className="text-[10px] font-black text-white">{formatMoney(trade.amount)}</div>
                            </div>

                            <div className="text-right">
                              <div className="text-[8px] uppercase text-slate-600">P/L</div>
                              <div className={`text-[10px] font-black ${clientPl >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                {clientPl >= 0 ? "+" : "-"}{formatMoney(Math.abs(clientPl))}
                              </div>
                            </div>
                          </div>

                          <div className="mt-1.5 grid grid-cols-5 gap-1 border-t border-slate-800/70 pt-1.5 text-[8px]">
                            <div>
                              <span className="block text-slate-600">Entrada</span>
                              <b className="text-slate-300">{formatMoney(trade.entry_price)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Atual/saída</span>
                              <b className="text-cyan-300">{exitPrice == null ? "—" : formatMoney(exitPrice)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Sacas</span>
                              <b className="text-slate-300">{formatNumber(trade.qty_sacks, 2)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">COFY</span>
                              <b className={houseNet >= 0 ? "text-emerald-300" : "text-red-300"}>
                                {houseNet >= 0 ? "+" : "-"}{formatMoney(Math.abs(houseNet))}
                              </b>
                            </div>
                            <div className="text-right">
                              <span className="block text-slate-600">Status</span>
                              <b className={trade.status === "open" ? "text-emerald-300" : "text-slate-300"}>{statusLabel}</b>
                            </div>
                          </div>
                        </div>

                        {/* DESKTOP • uma linha slim */}
                        <div className="hidden grid-cols-[76px_minmax(150px,1.4fr)_90px_105px_105px_85px_105px_115px_76px] items-center gap-2 text-[10px] lg:grid">
                          <span className={`w-fit rounded-md px-2 py-1 text-[9px] font-black ${
                            isBuy
                              ? "bg-emerald-500/15 text-emerald-300"
                              : "bg-red-500/15 text-red-300"
                          }`}>
                            {isBuy ? "COMPRA" : "VENDA"}
                          </span>

                          <div className="min-w-0">
                            <div className="truncate font-bold text-slate-200">{userLabel}</div>
                            <div className="truncate text-[8px] text-slate-600">
                              #{trade.id} • {formatDateTime(trade.created_at)}
                            </div>
                          </div>

                          <b className="text-white">{formatMoney(trade.amount)}</b>
                          <span className="text-slate-300">{formatMoney(trade.entry_price)}</span>
                          <span className="text-cyan-300">{exitPrice == null ? "—" : formatMoney(exitPrice)}</span>
                          <span className="text-slate-300">{formatNumber(trade.qty_sacks, 2)}</span>
                          <b className={clientPl >= 0 ? "text-emerald-300" : "text-red-300"}>
                            {clientPl >= 0 ? "+" : "-"}{formatMoney(Math.abs(clientPl))}
                          </b>
                          <b className={houseNet >= 0 ? "text-emerald-300" : "text-red-300"}>
                            {houseNet >= 0 ? "+" : "-"}{formatMoney(Math.abs(houseNet))}
                          </b>
                          <span className={`text-[9px] font-black ${
                            trade.status === "open" ? "text-emerald-300" : "text-slate-400"
                          }`}>
                            {statusLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 rounded-2xl border border-slate-800 bg-slate-900 p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={tradesLoading || tradesPage <= 1}
                onClick={() => {
                  const next = tradesPage - 1;
                  setTradesPage(next);
                  fetchTrades({ page: next });
                }}
                className="border-slate-700"
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Anterior
              </Button>

              <div className="text-xs text-slate-500">Página {tradesPage} de {tradesTotalPages}</div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={tradesLoading || tradesPage >= tradesTotalPages}
                onClick={() => {
                  const next = tradesPage + 1;
                  setTradesPage(next);
                  fetchTrades({ page: next });
                }}
                className="border-slate-700"
              >
                Próxima <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="text-white">Usuários Cadastrados</CardTitle>
                    <p className="text-xs text-slate-400 mt-1">
                      {usersTotal} usuário(s) encontrado(s)
                    </p>
                  </div>
                </div>

                <div className="mt-3 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-2.5 sm:p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-cyan-300">
                        <Megaphone className="h-3.5 w-3.5" /> Comunicado geral
                      </div>
                      <div className="mt-1 text-sm font-black text-white">Enviar mensagem para todos os usuários</div>
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        A mensagem aparecerá no Trader de cada conta e continuará individualmente como lida/não lida.
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setBroadcastOpen((v) => !v)}
                      className="bg-cyan-600 font-black hover:bg-cyan-500"
                    >
                      <Megaphone className="mr-2 h-4 w-4" />
                      {broadcastOpen ? "FECHAR" : "NOVO COMUNICADO"}
                    </Button>
                  </div>

                  {broadcastOpen && (
                    <form onSubmit={sendBroadcastToAllUsers} className="mt-3 space-y-2 border-t border-cyan-500/15 pt-3">
                      <div className="grid gap-2 sm:grid-cols-[1fr_160px]">
                        <Input
                          value={broadcastTitle}
                          onChange={(e) => setBroadcastTitle(e.target.value)}
                          placeholder="Título — ex.: Conheça o novo COFY LOCK"
                          maxLength={150}
                          disabled={broadcastSending}
                          className="border-slate-700 bg-slate-950 text-white"
                        />
                        <select
                          value={broadcastPriority}
                          onChange={(e) => setBroadcastPriority(e.target.value)}
                          disabled={broadcastSending}
                          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                        >
                          <option value="normal">Normal</option>
                          <option value="important">Importante</option>
                        </select>
                      </div>

                      <textarea
                        value={broadcastMessage}
                        onChange={(e) => setBroadcastMessage(e.target.value)}
                        placeholder="Digite a mensagem que todos os usuários receberão..."
                        maxLength={5000}
                        disabled={broadcastSending}
                        rows={4}
                        className="w-full resize-y rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/50"
                      />

                      {broadcastSending && (
                        <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-200">
                          Enviando: {broadcastProgress.sent}/{broadcastProgress.total}
                          {broadcastProgress.failed > 0 ? ` • falhas: ${broadcastProgress.failed}` : ""}
                        </div>
                      )}

                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-[10px] text-slate-500">
                          Antes do envio será solicitada confirmação. O sistema reaproveita a rota individual já existente.
                        </div>
                        <Button
                          type="submit"
                          disabled={broadcastSending || !String(broadcastTitle || "").trim() || !String(broadcastMessage || "").trim()}
                          className="bg-emerald-600 font-black hover:bg-emerald-500"
                        >
                          {broadcastSending ? "ENVIANDO..." : "ENVIAR PARA TODOS"}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>

                <form onSubmit={handleUsersSearch} className="mt-3">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        value={usersSearchInput}
                        onChange={(e) => setUsersSearchInput(e.target.value)}
                        placeholder="Buscar nome, e-mail, CPF ou telefone"
                        className="pl-9 bg-slate-950 border-slate-700 text-white"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={usersLoading}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Buscar
                    </Button>

                    {(usersQuery || usersSearchInput) && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleUsersClearSearch}
                        disabled={usersLoading}
                        className="border-slate-700"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Limpar
                      </Button>
                    )}
                  </div>
                </form>
              </CardHeader>

              <CardContent>
                {usersLoading ? (
                  <div className="py-8 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Carregando usuários...
                  </div>
                ) : adminUsers.length === 0 ? (
                  <div className="py-8 text-center text-slate-400">
                    Nenhum usuário encontrado.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adminUsers.map((u) => {
                      const isOpen = Number(expandedUserId) === Number(u.id);
                      const userLockPolicy = lockUserPolicies?.[u.id] || null;
                      const userLockPolicyLoading =
                        Number(lockUserPolicyLoading) === Number(u.id);
                      const userLockLimitSaving =
                        Number(lockUserLimitSaving) === Number(u.id);

                      return (
                        <div
                          key={u.id}
                          className="rounded-xl border border-slate-800 bg-slate-950/50 overflow-hidden"
                        >
                          {/* Linha compacta */}
                          <div className="p-3">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="font-semibold text-white truncate">
                                    {u.name || u.email || `Usuário #${u.id}`}
                                  </div>

                                  {u.role === "admin" && (
                                    <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[10px]">
                                      Admin
                                    </span>
                                  )}
                                </div>

                                <div className="text-xs text-slate-400 truncate mt-0.5">
                                  {u.email || "Sem e-mail"}
                                </div>

                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                                  <span>
                                    Saldo:{" "}
                                    <strong className="text-white">
                                      {formatMoney(u.balance)}
                                    </strong>
                                  </span>

                                  <span>
                                    Spread:{" "}
                                    <strong className="text-white">
                                      {u.spread_percentage === null ||
                                      u.spread_percentage === undefined
                                        ? "Padrão"
                                        : `${formatNumber(u.spread_percentage, 2)}%`}
                                    </strong>
                                  </span>

                                  {userLockPolicy && (
                                    <span>
                                      Limite LOCK:{" "}
                                      <strong className="text-cyan-300">
                                        {formatMoney(userLockPolicy.effective_limit)}
                                      </strong>
                                    </span>
                                  )}

                                  {u.cpf && (
                                    <span className="hidden sm:inline">
                                      CPF: <strong className="text-slate-300">{u.cpf}</strong>
                                    </span>
                                  )}
                                </div>
                              </div>

                              <Button
                                type="button"
                                size="sm"
                                variant={isOpen ? "outline" : "default"}
                                onClick={() => toggleAdminUser(u)}
                                className={
                                  isOpen
                                    ? "border-slate-700 w-full sm:w-auto"
                                    : "bg-emerald-500 hover:bg-emerald-600 w-full sm:w-auto"
                                }
                              >
                                {isOpen ? "Fechar" : "Abrir"}
                              </Button>
                            </div>
                          </div>

                          {/* Ficha completa: somente uma aberta por vez */}
                          {isOpen && (
                            <div className="border-t border-slate-800 p-2 sm:p-3">
                              <UserConfigCard
                                user={u}
                                onUpdate={async () => {
                                  await fetchDashboard();
                                  await fetchAdminUsers({
                                    page: usersPage,
                                    q: usersQuery,
                                  });
                                  await fetchLockUserPolicy(u.id, { silent: true });
                                }}
                              />

                              <div className="mt-3 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                  <div>
                                    <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-cyan-300">
                                      <LockKeyhole className="h-3.5 w-3.5" /> Limite COFY LOCK
                                    </div>
                                    <div className="mt-1 text-sm font-black text-white">
                                      Controle individual de proteção
                                    </div>
                                    <div className="mt-0.5 text-[10px] text-slate-500">
                                      Automático: R$ 5 mil com cadastro incompleto e R$ 50 mil com cadastro completo.
                                    </div>
                                  </div>

                                  {userLockPolicy && (
                                    <span
                                      className={`rounded-full border px-2.5 py-1 text-[9px] font-black ${
                                        userLockPolicy.custom_limit != null
                                          ? "border-violet-500/25 bg-violet-500/10 text-violet-300"
                                          : userLockPolicy.profile_completed
                                          ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                                          : "border-amber-500/25 bg-amber-500/10 text-amber-300"
                                      }`}
                                    >
                                      {userLockPolicy.custom_limit != null
                                        ? "PERSONALIZADO"
                                        : userLockPolicy.profile_completed
                                        ? "CADASTRO COMPLETO"
                                        : "CADASTRO INCOMPLETO"}
                                    </span>
                                  )}
                                </div>

                                {userLockPolicyLoading ? (
                                  <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/50 p-4 text-center text-xs text-slate-500">
                                    <RefreshCw className="mx-auto mb-2 h-4 w-4 animate-spin" />
                                    Carregando limite COFY LOCK...
                                  </div>
                                ) : userLockPolicy ? (
                                  <>
                                    <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
                                      {[
                                        ["Limite automático", formatMoney(userLockPolicy.base_limit)],
                                        [
                                          "Limite personalizado",
                                          userLockPolicy.custom_limit == null
                                            ? "Não definido"
                                            : formatMoney(userLockPolicy.custom_limit),
                                        ],
                                        ["Limite efetivo", formatMoney(userLockPolicy.effective_limit)],
                                        ["Já comprometido", formatMoney(userLockPolicy.committed_amount)],
                                        ["Disponível", formatMoney(userLockPolicy.available_limit)],
                                      ].map(([label, value]) => (
                                        <div key={label} className="rounded-lg border border-slate-800 bg-slate-950/55 p-2.5">
                                          <div className="text-[8px] uppercase tracking-wider text-slate-600">{label}</div>
                                          <div className="mt-1 text-xs font-black text-white">{value}</div>
                                        </div>
                                      ))}
                                    </div>

                                    <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950/45 p-2.5">
                                      <div className="flex items-center justify-between gap-2 text-[10px]">
                                        <span className="text-slate-500">Saldo livre da carteira</span>
                                        <b className="text-emerald-300">{formatMoney(userLockPolicy.wallet_balance)}</b>
                                      </div>
                                      <div className="mt-1 text-[9px] text-slate-600">
                                        O usuário só consegue contratar se tiver, ao mesmo tempo, limite disponível e saldo suficiente.
                                      </div>
                                    </div>

                                    <div className="mt-3 grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
                                      <div>
                                        <Label className="text-[10px] text-slate-400">
                                          Limite personalizado em R$
                                        </Label>
                                        <Input
                                          value={lockUserLimitInputs?.[u.id] ?? ""}
                                          onChange={(e) =>
                                            setLockUserLimitInputs((current) => ({
                                              ...current,
                                              [u.id]: e.target.value.replace(/[^0-9.,]/g, ""),
                                            }))
                                          }
                                          placeholder="Deixe vazio para usar o limite automático"
                                          inputMode="decimal"
                                          disabled={userLockLimitSaving}
                                          className="mt-1 border-slate-700 bg-slate-950 text-white"
                                        />
                                        <div className="mt-1 text-[9px] text-slate-600">
                                          Ex.: 100000 = R$ 100 mil. Valor 0 bloqueia novas contratações.
                                        </div>
                                      </div>

                                      <Button
                                        type="button"
                                        disabled={userLockLimitSaving}
                                        onClick={() => saveLockUserLimit(u.id)}
                                        className="self-end bg-cyan-600 font-black hover:bg-cyan-500"
                                      >
                                        {userLockLimitSaving ? "SALVANDO..." : "SALVAR LIMITE"}
                                      </Button>

                                      <Button
                                        type="button"
                                        variant="outline"
                                        disabled={userLockLimitSaving}
                                        onClick={() => saveLockUserLimit(u.id, { reset: true })}
                                        className="self-end border-slate-700"
                                      >
                                        USAR AUTOMÁTICO
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-300">
                                    Não foi possível carregar a política COFY LOCK deste usuário.
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => fetchLockUserPolicy(u.id)}
                                      className="ml-2 border-red-500/25 text-red-300"
                                    >
                                      TENTAR NOVAMENTE
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Paginação */}
                <div className="mt-4 pt-4 border-t border-slate-800 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-slate-400 text-center sm:text-left">
                    Página {usersPage} de {usersTotalPages}
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={usersLoading || usersPage <= 1}
                      onClick={() => goToUsersPage(usersPage - 1)}
                      className="border-slate-700"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={usersLoading || usersPage >= usersTotalPages}
                      onClick={() => goToUsersPage(usersPage + 1)}
                      className="border-slate-700"
                    >
                      Próxima
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}